import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { ThemeProvider } from 'next-themes'
import { initConsentAnalytics } from './lib/consentAnalytics'

// Initialize consent-first analytics (only loads GTM/Meta after cookie consent)
initConsentAnalytics();

const resources = {
  en: {
    translation: {
      "nav": {
        "home": "Home",
        "properties": "Properties",
        "about": "About",
        "contact": "Contact"
      },
      "hero": {
        "title": "Israel's Most Distinguished Addresses",
        "tagline": "Israel Prime Estate",
        "subtitle": "Curating the exceptional. We provide discrete access to off-market properties and developer partnerships for the world's most sophisticated investors.",
        "cta": {
          "discover": "Discover Opportunities",
          "contact": "Contact Us",
          "invest": "Invest"
        },
        "owners": "They Trust Our Expertise"
      },
      "whyNyref": {
        "title": "Why Choose Israel Prime Estate?",
        "subtitle": "Join hundreds of investors who trust our expertise for their Israeli real estate acquisitions",
        "features": {
          "portfolio": {
            "title": "Carefully Selected Properties",
            "description": "Access an exclusive selection of premium properties in Israel's most strategic locations."
          },
          "investment": {
            "title": "Expert Advisory Service",
            "description": "Benefit from our 10 years of experience in the Israeli market. We identify the best opportunities for you."
          },
          "income": {
            "title": "Turnkey Properties",
            "description": "Acquire properties already leased with reliable tenants and immediate cash flow."
          },
          "market": {
            "title": "Strategic Israeli Market",
            "description": "Israel offers unique opportunities with constant demand and attractive returns. We help you seize them."
          }
        }
      },
      "about": {
        "subtitle": "We Don't Sell Properties. We Curate Strategic Assets.",
        "description": "Real estate in Israel is not merely a transaction; it is an asset class of its own. At Israel Prime Estate, we bridge the gap between luxury living and financial intelligence. We do not chase the market, we define it.",
        "description2": "Through privileged alliances with the nation's top developers, we secure entry points that remain invisible to the public eye.",
        "partnerships": {
          "title": "Off-Market Access",
          "description": "The Invisible Market — True luxury is rarely listed. Our clients gain priority access to the 40% of prime inventory that never reaches public portals."
        },
        "financing": {
          "title": "Capital Architecture",
          "description": "Bespoke Structuring — We align with your liquidity needs. From balanced milestones to high-leverage structures extending up to 20/80, we negotiate terms that allow you to control the asset with optimized capital deployment."
        },
        "selection": {
          "title": "The \"Partner\" Status",
          "description": "Strategic Alignment — Don't just buy retail. Enter at the developer's table. Whether as a secure buyer or a co-development partner, you capture value from the project's inception."
        }
      },
      "legal": {
        "title": "Legal Structure & Acquisition Process",
        "structure": {
          "title": "Transparent Legal Structure",
          "description": "Each property we present benefits from a clear legal structure. We assist our clients at every step of the acquisition, ensuring legal security and asset protection."
        },
        "tokenization": {
          "title": "Simplified Acquisition",
          "description": "Our process simplifies property acquisition in Israel. We handle all administrative and legal complexities so you can focus on your investment."
        },
        "eligibility": {
          "title": "International Clients",
          "usInvestors": "All Nationalities",
          "international": "Full Advisory Support"
        }
      },
      "advantages": {
        "title": "Why Our Clients Choose Us",
        "insider": {
          "title": "Insider Access",
          "description": "Direct partnerships with developers give us access to off-market deals and pre-launch pricing."
        },
        "savings": {
          "title": "Significant Savings",
          "description": "Our clients typically save 15-30% compared to market prices through our developer relationships."
        },
        "service": {
          "title": "White-Glove Service",
          "description": "From selection to closing, we handle every detail. You focus on the opportunity."
        },
        "coInvestment": {
          "title": "Secure Co-Investment",
          "description": "Invest alongside other qualified buyers with 100% legal protection. Whatever your share, your ownership is fully secured and registered."
        }
      },
      "faq": {
        "title": "Frequently Asked Questions",
        "subtitle": "Everything you need to know about exclusive real estate opportunities in Israel"
      },
      "cta": {
        "title": "Get Access to Off-Market Deals",
        "subtitle": "Discover exclusive opportunities reserved for our clients.",
        "button": "Schedule a Consultation",
        "stayInformed": "Stay Informed",
        "emailSignup": {
          "title": "Receive our exclusive selection of premium properties in Israel",
          "namePlaceholder": "Enter your name",
          "emailPlaceholder": "Enter your email",
          "subscribe": "Subscribe"
        }
      },
      "footer": {
        "description": "Your trusted partner for acquiring premium real estate in Israel. Expert guidance, exclusive opportunities.",
        "support": "Support",
        "contact": "Contact",
        "followUs": "Follow Us",
        "paymentMethods": "Acquisition Support",
        "wireTransfer": "Bank Transfer",
        "creditCards": "All Methods",
        "cryptoWallet": "International",
        "copyright": "© 2025 IPE. All rights reserved."
      },
      "contact": {
        "title": "Visit Our Tel Aviv Office",
        "subtitle": "Meet us in person at the heart of Israel's real estate market",
        "visitUs": "Our Office",
        "address": {
          "title": "Address"
        },
        "phone": {
          "title": "Phone"
        },
        "email": {
          "title": "Email"
        },
        "hours": {
          "title": "Business Hours"
        },
        "gallery": "Our Tel Aviv Office",
        "form": {
          "title": "Get in Touch",
          "subtitle": "Schedule a consultation with our experts",
          "name": "Your Name",
          "email": "Email Address",
          "phone": "Phone Number (optional)",
          "message": "Your Message",
          "submit": "Send Message"
        }
      },
      "properties": {
        "title": "Our Exclusive Selection of Premium Properties",
        "subtitle": "Discover our carefully curated portfolio of Israeli real estate",
        "viewProperty": "View Property Details",
        "comingSoon": "Coming Soon",
        "tokenPrice": "Price per Unit",
        "totalValue": "Property Value",
        "availableTokens": "Availability",
        "minInvestment": "Starting From",
        "neighbors": "Prime Location Near",
        "units": "Total Units",
        "toBeAnnounced": "Contact Us",
        "yield": "Expected Return",
        "israel": "Israel"
      },
      "howItWorks": {
        "title": "How Our Advisory Process Works",
        "steps": {
          "step1": {
            "title": "Discover Our Selection",
            "description": "Browse our exclusive portfolio of premium Israeli properties, carefully selected by our experts."
          },
          "step2": {
            "title": "Personalized Advisory",
            "description": "Meet with our advisors who will guide you through the acquisition process and answer all your questions."
          },
          "step3": {
            "title": "Secure Acquisition",
            "description": "We handle all legal and administrative aspects of the purchase to ensure a smooth and secure transaction."
          },
          "step4": {
            "title": "Ongoing Support",
            "description": "Benefit from our property management services and optimize the performance of your investment."
          }
        }
      },
      "marketStats": {
        "title": "The Israel Real Estate Market",
        "description": "A resilient market with a proven track record of long-term appreciation",
        "stats": {
          "marketSize": "Market Size",
          "annualGrowth": "Annual Growth",
          "avgReturn": "Average Return",
          "occupancyRate": "Occupancy Rate"
        }
      },
      "tokenAdvantages": {
        "title": "The Advantages of Our Advisory Service",
        "description": "Benefit from our expertise and exclusive network in the Israeli market",
        "advantages": {
          "liquidity": {
            "title": "Optimized Costs",
            "description": "Our direct access to owners and exclusive network allows us to offer you the best prices and minimize transaction costs."
          },
          "transparency": {
            "title": "Rapid Acquisition Process",
            "description": "Thanks to our experience and established relationships, we significantly accelerate the acquisition process while ensuring total security."
          },
          "accessibility": {
            "title": "Complete Legal Support",
            "description": "Our legal team handles all aspects of the transaction, from due diligence to title transfer, ensuring optimal asset protection."
          },
          "flexibility": {
            "title": "Flexible Solutions",
            "description": "Whether you're looking for turnkey properties or development opportunities, we adapt our services to your specific investment objectives."
          },
          "digitalManagement": {
            "title": "Professional Property Management",
            "description": "Benefit from our property management services: tenant selection, rent collection, maintenance. Total peace of mind for optimal returns."
          },
          "paperless": {
            "title": "Simplified Process",
            "description": "We handle all administrative complexity. Focus on your investment while we take care of documentation and procedures."
          }
        },
        "stats": {
          "processing": "Expert Guidance",
          "costReduction": "Optimized Costs",
          "access": "Exclusive Access"
        }
      },
      "press": {
        "title": "Israel Prime Estate in the Press",
        "subtitle": "What the media says about us"
      },
      "events": {
        "title": "Upcoming Events",
        "subtitle": "Join our exclusive events"
      },
      "nycRealEstate": {
        "title": "New York Real Estate Has Never Been This Accessible",
        "features": {
          "valuableMarket": {
            "title": "World's Most Valuable Market",
            "description": "NYC is the most valuable real estate market in the world, with billions flowing through its properties every year."
          },
          "democratizing": {
            "title": "Democratizing Investment",
            "description": "Until now, investing in NYC real estate was reserved for the wealthy or large funds. Israel Prime Estate changes that."
          },
          "trillion": {
            "title": "$16 Trillion Market",
            "description": "Be part of the $16 trillion Real World Asset market that is transforming how we invest in tangible, rent-generating properties."
          },
          "ownBigApple": {
            "title": "Own the Big Apple",
            "description": "Don't just visit the Big Apple, Own a piece of it!"
          }
        },
        "stats": {
          "rentalMarket": "NYC Rental Market",
          "studioPrice": "Studio Rental Price"
        }
      },
      "israelRealEstate": {
        "title": "Why Invest in Israeli Real Estate Now?",
        "features": {
          "valuableMarket": {
            "title": "Strong & Growing Market",
            "description": "Israel's real estate market has delivered consistent 5-8% annual appreciation. With a robust economy, tech hub status (\"Silicon Wadi\"), and limited supply in prime areas, values continue to rise."
          },
          "democratizing": {
            "title": "Strong Currency Advantage",
            "description": "The Israeli Shekel (ILS) has appreciated 25%+ against major currencies over the past decade. Your investment benefits from both property appreciation AND currency strength."
          },
          "trillion": {
            "title": "High Rental Yields",
            "description": "Israeli properties offer attractive rental yields (4-6%) with strong tenant demand. Limited supply meets constant demand from locals, diaspora, and international professionals."
          },
          "ownIsrael": {
            "title": "Own Your Piece of Israel",
            "description": "Beyond returns, there's an emotional connection. Build your legacy and strengthen your ties to Israel through tangible property ownership."
          }
        },
        "stats": {
          "rentalMarket": "Avg. Annual Appreciation",
          "studioPrice": "Typical Rental Yield"
        }
      },
      "newsletter": {
        "title": "Stay Informed",
        "subtitle": "Receive our latest investment opportunities",
        "namePlaceholder": "Your name",
        "emailPlaceholder": "Your email",
        "subscribe": "Subscribe",
        "submitting": "Sending..."
      },
      "blog": {
        "readFullArticle": "Read full article",
        "loading": "Loading article...",
        "minRead": "min read"
      },
      "blockchain": {
        "title": "Our Expertise at Your Service",
        "subtitle": "Israel Prime Estate combines deep market knowledge, an extensive network, and personalized service to offer you the best real estate opportunities in Israel.",
        "features": {
          "security": {
            "title": "Total Security",
            "description": "Complete legal due diligence and rigorous property verification to ensure secure and compliant transactions"
          },
          "transparency": {
            "title": "Complete Transparency",
            "description": "Detailed reports on each property: financials, condition, legal status, and market analysis"
          },
          "liquidity": {
            "title": "Exclusive Network",
            "description": "Privileged access to off-market opportunities and strategic partnerships with major market players"
          }
        }
      },
      "privacy": {
        "pageTitle": "Privacy Policy",
        "pageDescription": "Israel Prime Estate Privacy Policy. Learn how we collect, use, and protect your personal information.",
        "title": "Privacy Policy",
        "introduction": {
          "title": "1. Introduction",
          "content": "At Israel Prime Estate, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform."
        },
        "informationCollection": {
          "title": "2. Information We Collect",
          "intro": "We collect various types of information to provide you with the best possible service:",
          "personal": {
            "title": "Personal Information",
            "item1": "Full name and contact details",
            "item2": "Government-issued identification",
            "item3": "Date of birth and nationality",
            "item4": "Residential address"
          },
          "financial": {
            "title": "Financial Information",
            "item1": "Bank account details",
            "item2": "Investment history",
            "item3": "Source of funds documentation"
          },
          "technical": {
            "title": "Technical Information",
            "item1": "IP address and device information",
            "item2": "Browser type and version",
            "item3": "Usage patterns and preferences",
            "item4": "Cookies and tracking data"
          }
        },
        "informationUse": {
          "title": "3. How We Use Your Information",
          "intro": "We use the collected information for various purposes:",
          "account": {
            "title": "Account Management",
            "item1": "Creating and maintaining your account",
            "item2": "Verifying your identity",
            "item3": "Processing investments and transactions",
            "item4": "Providing customer support"
          },
          "platform": {
            "title": "Platform Improvement",
            "item1": "Analyzing usage patterns",
            "item2": "Developing new features",
            "item3": "Enhancing user experience",
            "item4": "Troubleshooting technical issues"
          },
          "communication": {
            "title": "Communication",
            "item1": "Sending transaction confirmations",
            "item2": "Providing investment updates",
            "item3": "Marketing communications (with consent)",
            "item4": "Legal and regulatory notices"
          }
        },
        "informationSharing": {
          "title": "4. Information Sharing and Disclosure",
          "intro": "We may share your information with:",
          "providers": {
            "title": "Service Providers",
            "item1": "Payment processors",
            "item2": "Identity verification services",
            "item3": "Cloud storage providers",
            "item4": "Analytics services"
          },
          "legal": {
            "title": "Legal and Regulatory Bodies",
            "item1": "Government agencies",
            "item2": "Tax authorities",
            "item3": "Law enforcement",
            "item4": "Regulatory bodies"
          }
        },
        "security": {
          "title": "5. Data Security",
          "intro": "We implement robust security measures:",
          "item1": "Encryption of sensitive data",
          "item2": "Regular security audits",
          "item3": "Access controls and authentication",
          "item4": "Secure data storage",
          "item5": "Employee training on data protection",
          "item6": "Incident response procedures"
        },
        "rights": {
          "title": "6. Your Rights and Choices",
          "intro": "You have certain rights regarding your personal information:",
          "item1": "Access your personal information",
          "item2": "Request corrections to inaccurate data",
          "item3": "Request deletion of your information",
          "item4": "Object to processing of your data",
          "item5": "Withdraw consent for marketing",
          "item6": "Data portability rights",
          "item7": "Lodge complaints with supervisory authorities"
        },
        "international": {
          "title": "7. International Data Transfers",
          "intro": "Your information may be transferred and processed in different countries:",
          "item1": "Data protection standards in other countries",
          "item2": "Safeguards for international transfers",
          "item3": "EU-US Privacy Shield compliance",
          "item4": "Standard contractual clauses"
        },
        "children": {
          "title": "8. Children's Privacy",
          "content": "Our platform is not intended for children under 18. We do not knowingly collect or maintain information from persons under 18 years of age."
        },
        "updates": {
          "title": "9. Updates to This Policy",
          "content": "We may update this privacy policy from time to time. The updated version will be indicated by an updated \"Revised\" date."
        },
        "contact": {
          "title": "10. Contact Information",
          "content": "If you have questions about this Privacy Policy, please contact our Data Protection Officer at:",
          "email": "Email",
          "phone": "Phone",
          "address": "Address"
        }
      },
      "terms": {
        "pageTitle": "Terms and Conditions",
        "pageDescription": "Israel Prime Estate Terms and Conditions. Read our terms of service for using our platform.",
        "title": "Terms & Conditions",
        "subtitle": "Please read these terms and conditions carefully before using our services.",
        "acceptance": {
          "title": "Acceptance of Terms",
          "description": "By accessing or using the Israel Prime Estate platform, you agree to be bound by these Terms and Conditions."
        },
        "risks": {
          "title": "Investment Risks",
          "description": "Real estate investments carry inherent risks. You should carefully consider:",
          "item1": "Market fluctuations may affect property values",
          "item2": "Past performance is not indicative of future results",
          "item3": "Investments may be illiquid for extended periods",
          "item4": "Currency exchange rates may impact returns",
          "item5": "Regulatory changes may affect investment structures"
        },
        "eligibility": {
          "title": "Eligibility Requirements",
          "description": "To use our platform, you must meet the following requirements:",
          "item1": "Be at least 18 years of age",
          "item2": "Have legal capacity to enter into contracts",
          "item3": "Not be a resident of restricted jurisdictions",
          "item4": "Complete our identity verification process"
        },
        "account": {
          "title": "Account Security",
          "description": "You are responsible for maintaining the security of your account:",
          "item1": "Keep your login credentials confidential",
          "item2": "Enable two-factor authentication",
          "item3": "Report unauthorized access immediately",
          "item4": "Regularly review your account activity"
        },
        "investment": {
          "title": "Investment Terms",
          "description": "When making investments through our platform:",
          "item1": "All investments are subject to availability",
          "item2": "Minimum investment amounts may apply",
          "item3": "Processing times may vary",
          "item4": "Returns are not guaranteed"
        },
        "usage": {
          "title": "Platform Usage",
          "description": "Users must adhere to the following guidelines:",
          "item1": "Use the platform only for lawful purposes",
          "item2": "Not attempt to circumvent security measures",
          "item3": "Not engage in fraudulent activities",
          "item4": "Respect intellectual property rights"
        },
        "fees": {
          "title": "Fees and Charges",
          "description": "Various fees may apply to our services:",
          "item1": "Management fees for property oversight",
          "item2": "Transaction fees for investments",
          "item3": "Currency conversion fees where applicable"
        },
        "intellectual": {
          "title": "Intellectual Property",
          "description": "All content on the platform, including text, graphics, logos, and software, is the property of Israel Prime Estate and is protected by intellectual property laws."
        },
        "privacySection": {
          "title": "Privacy",
          "description": "Your use of the platform is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information."
        },
        "termination": {
          "title": "Termination",
          "description": "We reserve the right to terminate or suspend your account at any time for violation of these terms or for any other reason we deem appropriate."
        },
        "governing": {
          "title": "Governing Law",
          "description": "These terms are governed by the laws of Israel. Any disputes shall be resolved in the courts of Tel Aviv, Israel."
        },
        "contact": {
          "title": "Contact Information",
          "intro": "For questions about these terms, please contact us at:",
          "email": "Email",
          "phone": "Phone",
          "address": "Address"
        }
      }
    }
  },
  es: {
    translation: {
      "nav": {
        "home": "Inicio",
        "properties": "Propiedades",
        "about": "Acerca de",
        "contact": "Contacto"
      },
      "hero": {
        "title": "Las Direcciones Más Distinguidas de Israel",
        "tagline": "Israel Prime Estate",
        "subtitle": "Curando lo excepcional. Brindamos acceso discreto a propiedades fuera del mercado y asociaciones con desarrolladores para los inversores más sofisticados del mundo.",
        "cta": {
          "discover": "Descubrir Oportunidades",
          "contact": "Contáctenos",
          "invest": "Invertir"
        },
        "owners": "Confían en Nuestra Experiencia"
      },
      "about": {
        "subtitle": "No Vendemos Propiedades. Seleccionamos Activos Estratégicos.",
        "description": "El sector inmobiliario en Israel no es simplemente una transacción; es una clase de activo en sí mismo. En Israel Prime Estate, tendemos un puente entre el lujo residencial y la inteligencia financiera. No seguimos el mercado — lo definimos.",
        "description2": "A través de alianzas privilegiadas con los principales promotores del país, aseguramos puntos de entrada invisibles para el público general.",
        "partnerships": {
          "title": "Acceso Fuera del Mercado",
          "description": "El Mercado Invisible — El verdadero lujo rara vez se publica. Nuestros clientes obtienen acceso prioritario al 40% del inventario premium que nunca llega a los portales públicos."
        },
        "financing": {
          "title": "Arquitectura de Capital",
          "description": "Estructuración a Medida — Nos alineamos con sus necesidades de liquidez. Desde hitos equilibrados hasta estructuras de alto apalancamiento que alcanzan hasta 20/80, negociamos términos que le permiten controlar el activo con un despliegue de capital optimizado."
        },
        "selection": {
          "title": "El Estatus de \"Socio\"",
          "description": "Alineación Estratégica — No compre al por menor. Entre en la mesa del desarrollador. Ya sea como comprador seguro o socio de co-desarrollo, capture valor desde el inicio del proyecto."
        }
      },
      "whyNyref": {
        "title": "¿Por qué elegir Israel Prime Estate?",
        "subtitle": "Únete a cientos de inversores que confían en nuestra experiencia para sus adquisiciones inmobiliarias israelíes",
        "features": {
          "portfolio": {
            "title": "Propiedades Cuidadosamente Seleccionadas",
            "description": "Accede a una selección exclusiva de propiedades premium en las ubicaciones más estratégicas de Israel."
          },
          "investment": {
            "title": "Servicio de Asesoría Experto",
            "description": "Benefíciate de nuestros 10 años de experiencia en el mercado israelí. Identificamos las mejores oportunidades para ti."
          },
          "income": {
            "title": "Propiedades Llave en Mano",
            "description": "Adquiere propiedades ya arrendadas con inquilinos confiables y flujo de efectivo inmediato."
          },
          "market": {
            "title": "Mercado Israelí Estratégico",
            "description": "Israel ofrece oportunidades únicas con demanda constante y rendimientos atractivos. Te ayudamos a aprovecharlas."
          }
        }
      },
      "properties": {
        "title": "Nuestra Selección Exclusiva de Propiedades Premium",
        "subtitle": "Descubra nuestra cartera cuidadosamente seleccionada de bienes raíces israelíes",
        "viewProperty": "Ver Detalles de la Propiedad",
        "comingSoon": "Próximamente",
        "tokenPrice": "Precio por Unidad",
        "totalValue": "Valor de la Propiedad",
        "availableTokens": "Disponibilidad",
        "minInvestment": "Desde",
        "neighbors": "Ubicación Prime Cerca de",
        "units": "Unidades Totales",
        "toBeAnnounced": "Contáctenos",
        "yield": "Rendimiento Esperado",
        "israel": "Israel"
      },
      "howItWorks": {
        "title": "Cómo Funciona Nuestro Proceso de Asesoría",
        "steps": {
          "step1": {
            "title": "Descubra Nuestra Selección",
            "description": "Explore nuestro portafolio exclusivo de propiedades premium israelíes, cuidadosamente seleccionadas por nuestros expertos."
          },
          "step2": {
            "title": "Asesoría Personalizada",
            "description": "Reúnase con nuestros asesores que lo guiarán a través del proceso de adquisición y responderán todas sus preguntas."
          },
          "step3": {
            "title": "Adquisición Segura",
            "description": "Nos encargamos de todos los aspectos legales y administrativos de la compra para garantizar una transacción fluida y segura."
          },
          "step4": {
            "title": "Soporte Continuo",
            "description": "Benefíciese de nuestros servicios de gestión de propiedades y optimice el rendimiento de su inversión."
          }
        }
      },
      "marketStats": {
        "title": "El Mercado Inmobiliario de Israel",
        "description": "Un mercado resiliente con un historial probado de apreciación a largo plazo",
        "stats": {
          "marketSize": "Tamaño del Mercado",
          "annualGrowth": "Crecimiento Anual",
          "avgReturn": "Retorno Promedio",
          "occupancyRate": "Tasa de Ocupación"
        }
      },
      "tokenAdvantages": {
        "title": "Las Ventajas de Nuestro Servicio de Asesoría",
        "description": "Benefíciese de nuestra experiencia y red exclusiva en el mercado israelí",
        "advantages": {
          "liquidity": {
            "title": "Costos Optimizados",
            "description": "Nuestro acceso directo a propietarios y red exclusiva nos permite ofrecerle los mejores precios y minimizar los costos de transacción."
          },
          "transparency": {
            "title": "Proceso de Adquisición Rápido",
            "description": "Gracias a nuestra experiencia y relaciones establecidas, aceleramos significativamente el proceso de adquisición asegurando total seguridad."
          },
          "accessibility": {
            "title": "Soporte Legal Completo",
            "description": "Nuestro equipo legal maneja todos los aspectos de la transacción, desde la diligencia debida hasta la transferencia de título, asegurando una protección óptima de activos."
          },
          "flexibility": {
            "title": "Soluciones Flexibles",
            "description": "Ya sea que busque propiedades llave en mano u oportunidades de desarrollo, adaptamos nuestros servicios a sus objetivos de inversión específicos."
          },
          "digitalManagement": {
            "title": "Gestión Profesional de Propiedades",
            "description": "Benefíciese de nuestros servicios de gestión de propiedades: selección de inquilinos, cobro de rentas, mantenimiento. Total tranquilidad para rendimientos óptimos."
          },
          "paperless": {
            "title": "Proceso Simplificado",
            "description": "Nos encargamos de toda la complejidad administrativa. Concéntrese en su inversión mientras nosotros nos ocupamos de la documentación y procedimientos."
          }
        },
        "stats": {
          "processing": "Guía Experta",
          "costReduction": "Costos Optimizados",
          "access": "Acceso Exclusivo"
        }
      },
      "press": {
        "title": "Israel Prime Estate en la Prensa",
        "subtitle": "Lo que dicen los medios sobre nosotros"
      },
      "events": {
        "title": "Próximos Eventos",
        "subtitle": "Únete a nuestros eventos exclusivos"
      },
      "israelRealEstate": {
        "title": "¿Por Qué Invertir en Bienes Raíces de Israel Ahora?",
        "features": {
          "valuableMarket": {
            "title": "Mercado Fuerte y en Crecimiento",
            "description": "El mercado inmobiliario de Israel ha entregado una apreciación anual consistente del 5-8%. Con una economía robusta, estatus de hub tecnológico (\"Silicon Wadi\") y oferta limitada en áreas prime, los valores continúan aumentando."
          },
          "democratizing": {
            "title": "Ventaja de Moneda Fuerte",
            "description": "El Shekel israelí (ILS) se ha apreciado más del 25% frente a las principales monedas en la última década. Su inversión se beneficia tanto de la apreciación de la propiedad como de la fortaleza de la moneda."
          },
          "trillion": {
            "title": "Altos Rendimientos de Alquiler",
            "description": "Las propiedades israelíes ofrecen rendimientos de alquiler atractivos (4-6%) con fuerte demanda de inquilinos. La oferta limitada se encuentra con la demanda constante de locales, diáspora y profesionales internacionales."
          },
          "ownIsrael": {
            "title": "Posea Su Pedazo de Israel",
            "description": "Más allá de los rendimientos, hay una conexión emocional. Construya su legado y fortalezca sus lazos con Israel a través de la propiedad tangible."
          }
        },
        "stats": {
          "rentalMarket": "Apreciación Anual Promedio",
          "studioPrice": "Rendimiento de Alquiler Típico"
        }
      },
      "advantages": {
        "title": "Por Qué Nuestros Clientes Nos Eligen",
        "insider": {
          "title": "Acceso Privilegiado",
          "description": "Las asociaciones directas con promotores nos dan acceso a ofertas fuera del mercado y precios de pre-lanzamiento."
        },
        "savings": {
          "title": "Ahorros Significativos",
          "description": "Nuestros clientes típicamente ahorran 15-30% en comparación con los precios del mercado a través de nuestras relaciones con promotores."
        },
        "service": {
          "title": "Servicio de Guante Blanco",
          "description": "Desde la selección hasta el cierre, manejamos cada detalle. Usted se enfoca en la oportunidad."
        },
        "coInvestment": {
          "title": "Co-Inversión Segura",
          "description": "Invierta junto a otros compradores calificados con protección legal del 100%. Sea cual sea su parte, su propiedad está totalmente asegurada y registrada."
        }
      },
      "faq": {
        "title": "Preguntas Frecuentes",
        "subtitle": "Todo lo que necesita saber sobre oportunidades inmobiliarias exclusivas en Israel"
      },
      "cta": {
        "title": "Acceda a Ofertas Exclusivas",
        "subtitle": "Descubra oportunidades exclusivas reservadas para nuestros clientes.",
        "button": "Programar una Consulta",
        "stayInformed": "Mantenerse Informado",
        "emailSignup": {
          "title": "Reciba nuestra selección exclusiva de propiedades premium en Israel",
          "namePlaceholder": "Ingresa tu nombre",
          "emailPlaceholder": "Ingresa tu correo electrónico",
          "subscribe": "Suscribirse"
        }
      },
      "footer": {
        "description": "Su socio de confianza para adquirir bienes raíces premium en Israel. Guía experta, oportunidades exclusivas.",
        "support": "Soporte",
        "contact": "Contacto",
        "followUs": "Síguenos",
        "paymentMethods": "Soporte de Adquisición",
        "wireTransfer": "Transferencia Bancaria",
        "creditCards": "Todos los Métodos",
        "cryptoWallet": "Internacional",
        "copyright": "© 2025 IPE. Todos los derechos reservados."
      },
      "contact": {
        "title": "Visite Nuestra Oficina en Tel Aviv",
        "subtitle": "Reúnase con nosotros en persona en el corazón del mercado inmobiliario de Israel",
        "visitUs": "Nuestra Oficina",
        "address": {
          "title": "Dirección"
        },
        "phone": {
          "title": "Teléfono"
        },
        "email": {
          "title": "Correo Electrónico"
        },
        "hours": {
          "title": "Horario de Atención"
        },
        "gallery": "Nuestra Oficina en Tel Aviv",
        "form": {
          "title": "Póngase en Contacto",
          "subtitle": "Programe una consulta con nuestros expertos",
          "name": "Su Nombre",
          "email": "Correo Electrónico",
          "phone": "Número de Teléfono (opcional)",
          "message": "Su Mensaje",
          "submit": "Enviar Mensaje"
        }
      },
      "newsletter": {
        "title": "Manténgase Informado",
        "subtitle": "Reciba nuestras últimas oportunidades de inversión",
        "namePlaceholder": "Su nombre",
        "emailPlaceholder": "Su correo",
        "subscribe": "Suscribirse",
        "submitting": "Enviando..."
      },
      "blog": {
        "readFullArticle": "Leer artículo completo",
        "loading": "Cargando artículo...",
        "minRead": "min de lectura"
      },
      "blockchain": {
        "title": "Nuestra Experiencia a Su Servicio",
        "subtitle": "Israel Prime Estate combina profundo conocimiento del mercado, una extensa red y servicio personalizado para ofrecerle las mejores oportunidades inmobiliarias en Israel.",
        "features": {
          "security": {
            "title": "Seguridad Total",
            "description": "Diligencia debida legal completa y verificación rigurosa de propiedades para garantizar transacciones seguras y conformes"
          },
          "transparency": {
            "title": "Transparencia Completa",
            "description": "Informes detallados sobre cada propiedad: finanzas, condición, estado legal y análisis de mercado"
          },
          "liquidity": {
            "title": "Red Exclusiva",
            "description": "Acceso privilegiado a oportunidades fuera del mercado y alianzas estratégicas con los principales actores del mercado"
          }
        }
      },
      "privacy": {
        "pageTitle": "Política de Privacidad",
        "pageDescription": "Política de Privacidad de Israel Prime Estate. Aprenda cómo recopilamos, usamos y protegemos su información personal.",
        "title": "Política de Privacidad",
        "introduction": {
          "title": "1. Introducción",
          "content": "En Israel Prime Estate, nos tomamos su privacidad en serio. Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos su información."
        },
        "informationCollection": {
          "title": "2. Información que Recopilamos",
          "intro": "Recopilamos varios tipos de información para brindarle el mejor servicio posible:",
          "personal": {
            "title": "Información Personal",
            "item1": "Nombre completo y datos de contacto",
            "item2": "Identificación emitida por el gobierno",
            "item3": "Fecha de nacimiento y nacionalidad",
            "item4": "Dirección residencial"
          },
          "financial": {
            "title": "Información Financiera",
            "item1": "Datos de cuenta bancaria",
            "item2": "Historial de inversiones",
            "item3": "Documentación de origen de fondos"
          },
          "technical": {
            "title": "Información Técnica",
            "item1": "Dirección IP e información del dispositivo",
            "item2": "Tipo y versión del navegador",
            "item3": "Patrones de uso y preferencias",
            "item4": "Cookies y datos de seguimiento"
          }
        },
        "informationUse": {
          "title": "3. Cómo Usamos Su Información",
          "intro": "Usamos la información recopilada para varios propósitos:",
          "account": {
            "title": "Gestión de Cuenta",
            "item1": "Crear y mantener su cuenta",
            "item2": "Verificar su identidad",
            "item3": "Procesar inversiones y transacciones",
            "item4": "Proporcionar soporte al cliente"
          },
          "platform": {
            "title": "Mejora de la Plataforma",
            "item1": "Analizar patrones de uso",
            "item2": "Desarrollar nuevas funciones",
            "item3": "Mejorar la experiencia del usuario",
            "item4": "Solucionar problemas técnicos"
          },
          "communication": {
            "title": "Comunicación",
            "item1": "Enviar confirmaciones de transacciones",
            "item2": "Proporcionar actualizaciones de inversiones",
            "item3": "Comunicaciones de marketing (con consentimiento)",
            "item4": "Avisos legales y regulatorios"
          }
        },
        "informationSharing": {
          "title": "4. Compartición y Divulgación de Información",
          "intro": "Podemos compartir su información con:",
          "providers": {
            "title": "Proveedores de Servicios",
            "item1": "Procesadores de pagos",
            "item2": "Servicios de verificación de identidad",
            "item3": "Proveedores de almacenamiento en la nube",
            "item4": "Servicios de análisis"
          },
          "legal": {
            "title": "Entidades Legales y Regulatorias",
            "item1": "Agencias gubernamentales",
            "item2": "Autoridades fiscales",
            "item3": "Fuerzas del orden",
            "item4": "Organismos reguladores"
          }
        },
        "security": {
          "title": "5. Seguridad de Datos",
          "intro": "Implementamos medidas de seguridad robustas:",
          "item1": "Encriptación de datos sensibles",
          "item2": "Auditorías de seguridad regulares",
          "item3": "Controles de acceso y autenticación",
          "item4": "Almacenamiento seguro de datos",
          "item5": "Capacitación de empleados en protección de datos",
          "item6": "Procedimientos de respuesta a incidentes"
        },
        "rights": {
          "title": "6. Sus Derechos y Opciones",
          "intro": "Usted tiene ciertos derechos sobre su información personal:",
          "item1": "Acceder a su información personal",
          "item2": "Solicitar correcciones de datos inexactos",
          "item3": "Solicitar eliminación de su información",
          "item4": "Objetar el procesamiento de sus datos",
          "item5": "Retirar consentimiento para marketing",
          "item6": "Derechos de portabilidad de datos",
          "item7": "Presentar quejas ante autoridades supervisoras"
        },
        "international": {
          "title": "7. Transferencias Internacionales de Datos",
          "intro": "Su información puede ser transferida y procesada en diferentes países:",
          "item1": "Estándares de protección de datos en otros países",
          "item2": "Salvaguardas para transferencias internacionales",
          "item3": "Cumplimiento del Escudo de Privacidad UE-EE.UU.",
          "item4": "Cláusulas contractuales estándar"
        },
        "children": {
          "title": "8. Privacidad de Menores",
          "content": "Nuestra plataforma no está destinada a menores de 18 años. No recopilamos ni mantenemos información de personas menores de 18 años."
        },
        "updates": {
          "title": "9. Actualizaciones de Esta Política",
          "content": "Podemos actualizar esta política de privacidad de vez en cuando. La versión actualizada se indicará con una fecha de \"Revisado\" actualizada."
        },
        "contact": {
          "title": "10. Información de Contacto",
          "content": "Si tiene preguntas sobre esta Política de Privacidad, contacte a nuestro Oficial de Protección de Datos en:",
          "email": "Correo",
          "phone": "Teléfono",
          "address": "Dirección"
        }
      },
      "terms": {
        "pageTitle": "Términos y Condiciones",
        "pageDescription": "Términos y Condiciones de Israel Prime Estate. Lea nuestros términos de servicio para usar nuestra plataforma.",
        "title": "Términos y Condiciones",
        "subtitle": "Por favor lea estos términos y condiciones cuidadosamente antes de usar nuestros servicios.",
        "acceptance": {
          "title": "Aceptación de Términos",
          "description": "Al acceder o usar la plataforma de Israel Prime Estate, usted acepta estar sujeto a estos Términos y Condiciones."
        },
        "risks": {
          "title": "Riesgos de Inversión",
          "description": "Las inversiones inmobiliarias conllevan riesgos inherentes. Debe considerar cuidadosamente:",
          "item1": "Las fluctuaciones del mercado pueden afectar los valores de las propiedades",
          "item2": "El rendimiento pasado no es indicativo de resultados futuros",
          "item3": "Las inversiones pueden ser ilíquidas por períodos prolongados",
          "item4": "Los tipos de cambio pueden impactar los rendimientos",
          "item5": "Los cambios regulatorios pueden afectar las estructuras de inversión"
        },
        "eligibility": {
          "title": "Requisitos de Elegibilidad",
          "description": "Para usar nuestra plataforma, debe cumplir los siguientes requisitos:",
          "item1": "Tener al menos 18 años de edad",
          "item2": "Tener capacidad legal para celebrar contratos",
          "item3": "No ser residente de jurisdicciones restringidas",
          "item4": "Completar nuestro proceso de verificación de identidad"
        },
        "account": {
          "title": "Seguridad de la Cuenta",
          "description": "Usted es responsable de mantener la seguridad de su cuenta:",
          "item1": "Mantener sus credenciales de inicio de sesión confidenciales",
          "item2": "Habilitar la autenticación de dos factores",
          "item3": "Reportar acceso no autorizado inmediatamente",
          "item4": "Revisar regularmente la actividad de su cuenta"
        },
        "investment": {
          "title": "Términos de Inversión",
          "description": "Al realizar inversiones a través de nuestra plataforma:",
          "item1": "Todas las inversiones están sujetas a disponibilidad",
          "item2": "Pueden aplicar montos mínimos de inversión",
          "item3": "Los tiempos de procesamiento pueden variar",
          "item4": "Los rendimientos no están garantizados"
        },
        "usage": {
          "title": "Uso de la Plataforma",
          "description": "Los usuarios deben adherirse a las siguientes pautas:",
          "item1": "Usar la plataforma solo para propósitos legales",
          "item2": "No intentar eludir las medidas de seguridad",
          "item3": "No participar en actividades fraudulentas",
          "item4": "Respetar los derechos de propiedad intelectual"
        },
        "fees": {
          "title": "Tarifas y Cargos",
          "description": "Pueden aplicar varias tarifas a nuestros servicios:",
          "item1": "Tarifas de administración por supervisión de propiedades",
          "item2": "Tarifas de transacción por inversiones",
          "item3": "Tarifas de conversión de moneda cuando corresponda"
        },
        "intellectual": {
          "title": "Propiedad Intelectual",
          "description": "Todo el contenido de la plataforma, incluyendo texto, gráficos, logotipos y software, es propiedad de Israel Prime Estate y está protegido por las leyes de propiedad intelectual."
        },
        "privacySection": {
          "title": "Privacidad",
          "description": "Su uso de la plataforma también está regido por nuestra Política de Privacidad, que describe cómo recopilamos, usamos y protegemos su información personal."
        },
        "termination": {
          "title": "Terminación",
          "description": "Nos reservamos el derecho de terminar o suspender su cuenta en cualquier momento por violación de estos términos o por cualquier otra razón que consideremos apropiada."
        },
        "governing": {
          "title": "Ley Aplicable",
          "description": "Estos términos se rigen por las leyes de Israel. Cualquier disputa se resolverá en los tribunales de Tel Aviv, Israel."
        },
        "contact": {
          "title": "Información de Contacto",
          "intro": "Para preguntas sobre estos términos, contáctenos en:",
          "email": "Correo",
          "phone": "Teléfono",
          "address": "Dirección"
        }
      }
    }
  },
  zh: {
    translation: {
      "nav": {
        "home": "首页",
        "properties": "房产",
        "about": "关于",
        "contact": "联系"
      },
      "hero": {
        "title": "以色列最尊贵的地址",
        "tagline": "Israel Prime Estate",
        "subtitle": "精选卓越。我们为全球最精明的投资者提供非公开房产的独家渠道及开发商合作机会。",
        "cta": {
          "discover": "发现机会",
          "contact": "联系我们",
          "invest": "投资"
        },
        "owners": "他们信任我们的专业知识"
      },
      "about": {
        "subtitle": "我们不销售房产。我们甄选战略资产。",
        "description": "以色列房地产不仅仅是一笔交易，它本身就是一种独特的资产类别。在Israel Prime Estate，我们架起奢华生活与金融智慧之间的桥梁。我们不追随市场——我们定义市场。",
        "description2": "通过与国内顶级开发商的特权联盟，我们获得公众无法触及的入场机会。",
        "partnerships": {
          "title": "非公开市场准入",
          "description": "隐形市场 — 真正的奢华鲜少公开上市。我们的客户优先获取那40%从未进入公共平台的优质房源。"
        },
        "financing": {
          "title": "资本架构",
          "description": "定制结构化 — 我们配合您的流动性需求。从平衡的里程碑到高达20/80的高杠杆结构，我们协商的条款让您以优化的资本配置控制资产。"
        },
        "selection": {
          "title": "\"合伙人\"身份",
          "description": "战略协同 — 不要只做零售买家。进入开发商的谈判桌。无论是作为安全买家还是联合开发合作伙伴，您都能从项目启动之初就获取价值。"
        }
      },
      "advantages": {
        "title": "为什么我们的客户选择我们",
        "insider": {
          "title": "内部访问",
          "description": "与开发商的直接合作使我们能够访问场外交易和预发布定价。"
        },
        "savings": {
          "title": "显著节省",
          "description": "通过我们与开发商的关系，我们的客户通常可以节省15-30%的市场价格。"
        },
        "service": {
          "title": "白手套服务",
          "description": "从选择到交易完成，我们处理每一个细节。您专注于机会。"
        },
        "coInvestment": {
          "title": "安全共同投资",
          "description": "与其他合格买家共同投资，享受100%法律保护。无论您的份额多少，您的所有权都完全有保障并已注册。"
        }
      },
      "israelRealEstate": {
        "title": "为什么现在投资以色列房地产？",
        "features": {
          "valuableMarket": {
            "title": "强劲且不断增长的市场",
            "description": "以色列房地产市场实现了持续的5-8%年度升值。凭借强劲的经济、科技中心地位（\"硅谷瓦迪\"）和黄金地段的有限供应，价值持续上涨。"
          },
          "democratizing": {
            "title": "强势货币优势",
            "description": "以色列谢克尔（ILS）在过去十年中对主要货币升值超过25%。您的投资同时受益于房产升值和货币强势。"
          },
          "trillion": {
            "title": "高租金收益",
            "description": "以色列房产提供有吸引力的租金收益率（4-6%），租户需求强劲。有限的供应满足来自本地人、侨民和国际专业人士的持续需求。"
          },
          "ownIsrael": {
            "title": "拥有您在以色列的一部分",
            "description": "超越回报，还有情感联系。通过有形的房产所有权建立您的遗产并加强与以色列的联系。"
          }
        },
        "stats": {
          "rentalMarket": "平均年度升值",
          "studioPrice": "典型租金收益率"
        }
      },
      "faq": {
        "title": "常见问题",
        "subtitle": "了解以色列独家房地产机会所需的一切信息"
      },
      "cta": {
        "title": "获取非公开房源",
        "subtitle": "发现为我们客户保留的独家机会。",
        "button": "预约咨询",
        "stayInformed": "保持知情",
        "emailSignup": {
          "title": "接收我们在以色列的独家优质房产选择",
          "namePlaceholder": "输入你的名字",
          "emailPlaceholder": "输入你的电子邮件",
          "subscribe": "订阅"
        }
      },
      "footer": {
        "description": "您在以色列购买优质房地产的可信赖合作伙伴。专业指导，独家机会。",
        "support": "支持",
        "contact": "联系",
        "followUs": "关注我们",
        "paymentMethods": "购买支持",
        "wireTransfer": "银行转账",
        "creditCards": "所有方式",
        "cryptoWallet": "国际",
        "copyright": "© 2025 IPE。保留所有权利。"
      },
      "contact": {
        "title": "访问我们的特拉维夫办公室",
        "subtitle": "在以色列房地产市场中心与我们会面",
        "visitUs": "我们的办公室",
        "address": {
          "title": "地址"
        },
        "phone": {
          "title": "电话"
        },
        "email": {
          "title": "电子邮件"
        },
        "hours": {
          "title": "营业时间"
        },
        "gallery": "我们的特拉维夫办公室",
        "form": {
          "title": "联系我们",
          "subtitle": "与我们的专家安排咨询",
          "name": "您的姓名",
          "email": "电子邮件地址",
          "phone": "电话号码（可选）",
          "message": "您的留言",
          "submit": "发送消息"
        }
      },
      "properties": {
        "title": "我们精选的优质房产",
        "subtitle": "发现我们精心挑选的以色列房地产组合",
        "viewProperty": "查看房产详情",
        "comingSoon": "即将推出",
        "tokenPrice": "单价",
        "totalValue": "房产价值",
        "availableTokens": "可用性",
        "minInvestment": "起价",
        "neighbors": "优越位置靠近",
        "units": "总单位",
        "toBeAnnounced": "联系我们",
        "yield": "预期回报",
        "israel": "以色列"
      },
      "newsletter": {
        "title": "保持了解",
        "subtitle": "获取我们最新的投资机会",
        "namePlaceholder": "您的姓名",
        "emailPlaceholder": "您的邮箱",
        "subscribe": "订阅",
        "submitting": "发送中..."
      },
      "blog": {
        "readFullArticle": "阅读完整文章",
        "loading": "加载文章中...",
        "minRead": "分钟阅读"
      },
      "blockchain": {
        "title": "我们的专业知识为您服务",
        "subtitle": "Israel Prime Estate结合深厚的市场知识、广泛的网络和个性化服务，为您提供以色列最佳房地产机会。",
        "features": {
          "security": {
            "title": "全面安全",
            "description": "完整的法律尽职调查和严格的房产验证，确保安全合规的交易"
          },
          "transparency": {
            "title": "完全透明",
            "description": "每个房产的详细报告：财务、状况、法律状态和市场分析"
          },
          "liquidity": {
            "title": "独家网络",
            "description": "优先获得场外机会和与主要市场参与者的战略合作伙伴关系"
          }
        }
      },
      "privacy": {
        "pageTitle": "隐私政策",
        "pageDescription": "Israel Prime Estate 隐私政策。了解我们如何收集、使用和保护您的个人信息。",
        "title": "隐私政策",
        "introduction": {
          "title": "1. 简介",
          "content": "在 Israel Prime Estate，我们非常重视您的隐私。本隐私政策解释了我们如何在您使用我们平台时收集、使用、披露和保护您的信息。"
        },
        "informationCollection": {
          "title": "2. 我们收集的信息",
          "intro": "我们收集各种类型的信息，以便为您提供最佳服务：",
          "personal": {
            "title": "个人信息",
            "item1": "全名和联系方式",
            "item2": "政府签发的身份证明",
            "item3": "出生日期和国籍",
            "item4": "居住地址"
          },
          "financial": {
            "title": "财务信息",
            "item1": "银行账户详情",
            "item2": "投资历史",
            "item3": "资金来源文件"
          },
          "technical": {
            "title": "技术信息",
            "item1": "IP地址和设备信息",
            "item2": "浏览器类型和版本",
            "item3": "使用模式和偏好",
            "item4": "Cookie和跟踪数据"
          }
        },
        "informationUse": {
          "title": "3. 我们如何使用您的信息",
          "intro": "我们将收集的信息用于各种目的：",
          "account": {
            "title": "账户管理",
            "item1": "创建和维护您的账户",
            "item2": "验证您的身份",
            "item3": "处理投资和交易",
            "item4": "提供客户支持"
          },
          "platform": {
            "title": "平台改进",
            "item1": "分析使用模式",
            "item2": "开发新功能",
            "item3": "增强用户体验",
            "item4": "解决技术问题"
          },
          "communication": {
            "title": "沟通",
            "item1": "发送交易确认",
            "item2": "提供投资更新",
            "item3": "营销通讯（经同意）",
            "item4": "法律和监管通知"
          }
        },
        "informationSharing": {
          "title": "4. 信息共享和披露",
          "intro": "我们可能与以下方共享您的信息：",
          "providers": {
            "title": "服务提供商",
            "item1": "支付处理商",
            "item2": "身份验证服务",
            "item3": "云存储提供商",
            "item4": "分析服务"
          },
          "legal": {
            "title": "法律和监管机构",
            "item1": "政府机构",
            "item2": "税务机关",
            "item3": "执法部门",
            "item4": "监管机构"
          }
        },
        "security": {
          "title": "5. 数据安全",
          "intro": "我们实施强大的安全措施：",
          "item1": "敏感数据加密",
          "item2": "定期安全审计",
          "item3": "访问控制和身份验证",
          "item4": "安全数据存储",
          "item5": "员工数据保护培训",
          "item6": "事件响应程序"
        },
        "rights": {
          "title": "6. 您的权利和选择",
          "intro": "您对个人信息拥有某些权利：",
          "item1": "访问您的个人信息",
          "item2": "要求更正不准确的数据",
          "item3": "要求删除您的信息",
          "item4": "反对数据处理",
          "item5": "撤回营销同意",
          "item6": "数据可携带权",
          "item7": "向监管机构投诉"
        },
        "international": {
          "title": "7. 国际数据传输",
          "intro": "您的信息可能被传输和处理到不同国家：",
          "item1": "其他国家的数据保护标准",
          "item2": "国际传输的保障措施",
          "item3": "欧盟-美国隐私护盾合规",
          "item4": "标准合同条款"
        },
        "children": {
          "title": "8. 儿童隐私",
          "content": "我们的平台不面向18岁以下的人。我们不会故意收集或维护18岁以下人员的信息。"
        },
        "updates": {
          "title": "9. 本政策的更新",
          "content": "我们可能会不时更新本隐私政策。更新版本将通过更新的\"修订\"日期来标明。"
        },
        "contact": {
          "title": "10. 联系信息",
          "content": "如果您对本隐私政策有疑问，请联系我们的数据保护官：",
          "email": "邮箱",
          "phone": "电话",
          "address": "地址"
        }
      },
      "terms": {
        "pageTitle": "条款和条件",
        "pageDescription": "Israel Prime Estate 条款和条件。请阅读我们使用平台的服务条款。",
        "title": "条款和条件",
        "subtitle": "请在使用我们的服务之前仔细阅读这些条款和条件。",
        "acceptance": {
          "title": "接受条款",
          "description": "通过访问或使用 Israel Prime Estate 平台，您同意受这些条款和条件的约束。"
        },
        "risks": {
          "title": "投资风险",
          "description": "房地产投资存在固有风险。您应仔细考虑：",
          "item1": "市场波动可能影响房产价值",
          "item2": "过去的表现不代表未来结果",
          "item3": "投资可能长期缺乏流动性",
          "item4": "汇率可能影响收益",
          "item5": "监管变化可能影响投资结构"
        },
        "eligibility": {
          "title": "资格要求",
          "description": "要使用我们的平台，您必须满足以下要求：",
          "item1": "年满18岁",
          "item2": "具有签订合同的法律能力",
          "item3": "不是受限管辖区的居民",
          "item4": "完成我们的身份验证流程"
        },
        "account": {
          "title": "账户安全",
          "description": "您有责任维护账户安全：",
          "item1": "保持登录凭证保密",
          "item2": "启用双因素认证",
          "item3": "立即报告未经授权的访问",
          "item4": "定期查看账户活动"
        },
        "investment": {
          "title": "投资条款",
          "description": "通过我们的平台进行投资时：",
          "item1": "所有投资视供应情况而定",
          "item2": "可能适用最低投资金额",
          "item3": "处理时间可能不同",
          "item4": "收益不保证"
        },
        "usage": {
          "title": "平台使用",
          "description": "用户必须遵守以下准则：",
          "item1": "仅将平台用于合法目的",
          "item2": "不试图绕过安全措施",
          "item3": "不从事欺诈活动",
          "item4": "尊重知识产权"
        },
        "fees": {
          "title": "费用和收费",
          "description": "我们的服务可能适用各种费用：",
          "item1": "房产监管管理费",
          "item2": "投资交易费",
          "item3": "适用的货币转换费"
        },
        "intellectual": {
          "title": "知识产权",
          "description": "平台上的所有内容，包括文字、图形、标志和软件，均为 Israel Prime Estate 的财产，受知识产权法保护。"
        },
        "privacySection": {
          "title": "隐私",
          "description": "您对平台的使用还受我们隐私政策的约束，该政策描述了我们如何收集、使用和保护您的个人信息。"
        },
        "termination": {
          "title": "终止",
          "description": "我们保留因违反这些条款或我们认为适当的任何其他原因随时终止或暂停您账户的权利。"
        },
        "governing": {
          "title": "适用法律",
          "description": "这些条款受以色列法律管辖。任何争议应在以色列特拉维夫法院解决。"
        },
        "contact": {
          "title": "联系信息",
          "intro": "如有关于这些条款的问题，请联系我们：",
          "email": "邮箱",
          "phone": "电话",
          "address": "地址"
        }
      }
    }
  },
  fr: {
    translation: {
      "nav": {
        "home": "Accueil",
        "properties": "Propriétés",
        "about": "À Propos",
        "contact": "Contact"
      },
      "hero": {
        "title": "Les Adresses les Plus Prestigieuses d'Israël",
        "tagline": "Israel Prime Estate",
        "subtitle": "L'exception à portée de main. Nous offrons un accès discret aux propriétés hors marché et aux partenariats promoteurs pour les investisseurs les plus avisés.",
        "cta": {
          "discover": "Découvrir les Opportunités",
          "contact": "Contactez-Nous",
          "invest": "Investir"
        },
        "owners": "Ils Nous Font Confiance"
      },
      "whyNyref": {
        "title": "Pourquoi Choisir Israel Prime Estate ?",
        "subtitle": "Rejoignez des centaines d'investisseurs qui font confiance à notre expertise pour leurs acquisitions immobilières en Israël",
        "features": {
          "portfolio": {
            "title": "Propriétés Soigneusement Sélectionnées",
            "description": "Accédez à une sélection exclusive de propriétés premium dans les emplacements les plus stratégiques d'Israël."
          },
          "investment": {
            "title": "Service de Conseil Expert",
            "description": "Bénéficiez de nos 10 ans d'expérience sur le marché israélien. Nous identifions les meilleures opportunités pour vous."
          },
          "income": {
            "title": "Propriétés Clés en Main",
            "description": "Acquérez des propriétés déjà louées avec des locataires fiables et des flux de trésorerie immédiats."
          },
          "market": {
            "title": "Marché Israélien Stratégique",
            "description": "Israël offre des opportunités uniques avec une demande constante et des rendements attractifs. Nous vous aidons à en profiter."
          }
        }
      },
      "about": {
        "subtitle": "Nous Ne Vendons Pas des Biens. Nous Sélectionnons des Actifs Stratégiques.",
        "description": "L'immobilier en Israël n'est pas une simple transaction ; c'est une classe d'actifs à part entière. Chez Israel Prime Estate, nous créons le pont entre art de vivre et intelligence financière. Nous ne suivons pas le marché — nous le définissons.",
        "description2": "Grâce à des alliances privilégiées avec les meilleurs promoteurs du pays, nous sécurisons des points d'entrée invisibles au grand public.",
        "partnerships": {
          "title": "Accès Hors Marché",
          "description": "Le Marché Invisible — Le vrai luxe est rarement affiché. Nos clients bénéficient d'un accès prioritaire aux 40% de l'offre premium qui n'atteint jamais les portails publics."
        },
        "financing": {
          "title": "Architecture Financière",
          "description": "Structuration Sur Mesure — Nous nous adaptons à vos besoins de liquidité. Des jalons équilibrés aux structures à fort effet de levier allant jusqu'à 20/80, nous négocions des conditions qui vous permettent de contrôler l'actif avec un déploiement de capital optimisé."
        },
        "selection": {
          "title": "Le Statut \"Partenaire\"",
          "description": "Alignement Stratégique — N'achetez pas au détail. Entrez à la table des promoteurs. Que ce soit comme acheteur sécurisé ou partenaire de co-développement, vous captez la valeur dès la conception du projet."
        }
      },
      "legal": {
        "title": "Structure Juridique et Processus d'Acquisition",
        "structure": {
          "title": "Structure Juridique Transparente",
          "description": "Chaque propriété que nous présentons bénéficie d'une structure juridique claire. Nous assistons nos clients à chaque étape de l'acquisition, garantissant la sécurité juridique et la protection des actifs."
        },
        "tokenization": {
          "title": "Acquisition Simplifiée",
          "description": "Notre processus simplifie l'acquisition immobilière en Israël. Nous gérons toutes les complexités administratives et juridiques pour que vous puissiez vous concentrer sur votre investissement."
        },
        "eligibility": {
          "title": "Clients Internationaux",
          "usInvestors": "Toutes Nationalités",
          "international": "Support Complet"
        }
      },
      "advantages": {
        "title": "Pourquoi Nos Clients Nous Choisissent",
        "insider": {
          "title": "Accès Privilégié",
          "description": "Les partenariats directs avec les promoteurs nous donnent accès à des offres hors marché et à des prix de prélancement."
        },
        "savings": {
          "title": "Économies Significatives",
          "description": "Nos clients économisent généralement 15 à 30 % par rapport aux prix du marché grâce à nos relations avec les promoteurs."
        },
        "service": {
          "title": "Service Haut de Gamme",
          "description": "De la sélection à la clôture, nous gérons chaque détail. Vous vous concentrez sur l'opportunité."
        },
        "coInvestment": {
          "title": "Co-Investissement Sécurisé",
          "description": "Investissez aux côtés d'autres acheteurs qualifiés avec une protection juridique à 100 %. Quelle que soit votre part, votre propriété est entièrement sécurisée et enregistrée."
        }
      },
      "faq": {
        "title": "Questions Fréquentes",
        "subtitle": "Tout ce que vous devez savoir sur les opportunités immobilières exclusives en Israël"
      },
      "cta": {
        "title": "Accédez aux Offres Hors Marché",
        "subtitle": "Découvrez des opportunités exclusives réservées à nos clients.",
        "button": "Planifier une Consultation",
        "stayInformed": "Restez Informé",
        "emailSignup": {
          "title": "Recevez notre sélection exclusive de propriétés premium en Israël",
          "namePlaceholder": "Entrez votre nom",
          "emailPlaceholder": "Entrez votre email",
          "subscribe": "S'abonner"
        }
      },
      "footer": {
        "description": "Votre partenaire de confiance pour acquérir des biens immobiliers premium en Israël. Conseil expert, opportunités exclusives.",
        "support": "Support",
        "contact": "Contact",
        "followUs": "Suivez-Nous",
        "paymentMethods": "Support d'Acquisition",
        "wireTransfer": "Virement Bancaire",
        "creditCards": "Toutes Méthodes",
        "cryptoWallet": "International",
        "copyright": "© 2025 IPE. Tous droits réservés."
      },
      "contact": {
        "title": "Visitez Notre Bureau à Tel Aviv",
        "subtitle": "Rencontrez-nous en personne au cœur du marché immobilier israélien",
        "visitUs": "Notre Bureau",
        "address": {
          "title": "Adresse"
        },
        "phone": {
          "title": "Téléphone"
        },
        "email": {
          "title": "Email"
        },
        "hours": {
          "title": "Heures d'Ouverture"
        },
        "gallery": "Notre Bureau à Tel Aviv",
        "form": {
          "title": "Contactez-Nous",
          "subtitle": "Planifiez une consultation avec nos experts",
          "name": "Votre Nom",
          "email": "Adresse Email",
          "phone": "Numéro de Téléphone (optionnel)",
          "message": "Votre Message",
          "submit": "Envoyer le Message"
        }
      },
      "properties": {
        "title": "Notre Sélection Exclusive de Propriétés Premium",
        "subtitle": "Découvrez notre portefeuille soigneusement sélectionné de biens immobiliers israéliens",
        "viewProperty": "Voir les Détails de la Propriété",
        "comingSoon": "Bientôt Disponible",
        "tokenPrice": "Prix par Unité",
        "totalValue": "Valeur de la Propriété",
        "availableTokens": "Disponibilité",
        "minInvestment": "À Partir de",
        "neighbors": "Emplacement Prime Près de",
        "units": "Total d'Unités",
        "toBeAnnounced": "Contactez-Nous",
        "yield": "Rendement Attendu",
        "israel": "Israël"
      },
      "howItWorks": {
        "title": "Comment Fonctionne Notre Processus de Conseil",
        "steps": {
          "step1": {
            "title": "Découvrez Notre Sélection",
            "description": "Parcourez notre portefeuille exclusif de propriétés premium israéliennes, soigneusement sélectionnées par nos experts."
          },
          "step2": {
            "title": "Conseil Personnalisé",
            "description": "Rencontrez nos conseillers qui vous guideront tout au long du processus d'acquisition et répondront à toutes vos questions."
          },
          "step3": {
            "title": "Acquisition Sécurisée",
            "description": "Nous gérons tous les aspects juridiques et administratifs de l'achat pour garantir une transaction fluide et sécurisée."
          },
          "step4": {
            "title": "Support Continu",
            "description": "Bénéficiez de nos services de gestion immobilière et optimisez la performance de votre investissement."
          }
        }
      },
      "marketStats": {
        "title": "Le Marché Immobilier Israélien",
        "description": "Un marché résilient avec un historique prouvé d'appréciation à long terme",
        "stats": {
          "marketSize": "Taille du Marché",
          "annualGrowth": "Croissance Annuelle",
          "avgReturn": "Rendement Moyen",
          "occupancyRate": "Taux d'Occupation"
        }
      },
      "tokenAdvantages": {
        "title": "Les Avantages de Notre Service de Conseil",
        "description": "Bénéficiez de notre expertise et réseau exclusif sur le marché israélien",
        "advantages": {
          "liquidity": {
            "title": "Coûts Optimisés",
            "description": "Notre accès direct aux propriétaires et notre réseau exclusif nous permettent de vous offrir les meilleurs prix et de minimiser les coûts de transaction."
          },
          "transparency": {
            "title": "Processus d'Acquisition Rapide",
            "description": "Grâce à notre expérience et nos relations établies, nous accélérons considérablement le processus d'acquisition tout en garantissant une sécurité totale."
          },
          "accessibility": {
            "title": "Support Juridique Complet",
            "description": "Notre équipe juridique gère tous les aspects de la transaction, de la diligence raisonnable au transfert de titre, garantissant une protection optimale des actifs."
          },
          "flexibility": {
            "title": "Solutions Flexibles",
            "description": "Que vous recherchiez des propriétés clés en main ou des opportunités de développement, nous adaptons nos services à vos objectifs d'investissement spécifiques."
          },
          "digitalManagement": {
            "title": "Gestion Immobilière Professionnelle",
            "description": "Bénéficiez de nos services de gestion immobilière : sélection des locataires, perception des loyers, maintenance. Tranquillité d'esprit totale pour des rendements optimaux."
          },
          "paperless": {
            "title": "Processus Simplifié",
            "description": "Nous gérons toute la complexité administrative. Concentrez-vous sur votre investissement pendant que nous nous occupons de la documentation et des procédures."
          }
        },
        "stats": {
          "processing": "Accompagnement Expert",
          "costReduction": "Coûts Optimisés",
          "access": "Accès Exclusif"
        }
      },
      "press": {
        "title": "Israel Prime Estate dans la Presse",
        "subtitle": "Ce que les médias disent de nous"
      },
      "events": {
        "title": "Événements à Venir",
        "subtitle": "Rejoignez nos événements exclusifs"
      },
      "israelRealEstate": {
        "title": "Pourquoi Investir dans l'Immobilier Israélien Maintenant ?",
        "features": {
          "valuableMarket": {
            "title": "Marché Fort et en Croissance",
            "description": "Le marché immobilier israélien a connu une appréciation annuelle constante de 5 à 8 %. Avec une économie robuste, un statut de hub technologique (\"Silicon Wadi\") et une offre limitée dans les zones privilégiées, les valeurs continuent d'augmenter."
          },
          "democratizing": {
            "title": "Avantage d'une Monnaie Forte",
            "description": "Le Shekel israélien (ILS) s'est apprécié de plus de 25 % par rapport aux principales devises au cours de la dernière décennie. Votre investissement bénéficie à la fois de l'appréciation immobilière ET de la force de la monnaie."
          },
          "trillion": {
            "title": "Rendements Locatifs Élevés",
            "description": "Les propriétés israéliennes offrent des rendements locatifs attractifs (4-6 %) avec une forte demande de locataires. L'offre limitée répond à la demande constante des locaux, de la diaspora et des professionnels internationaux."
          },
          "ownIsrael": {
            "title": "Possédez Votre Part d'Israël",
            "description": "Au-delà des rendements, il y a une connexion émotionnelle. Construisez votre héritage et renforcez vos liens avec Israël grâce à la propriété immobilière tangible."
          }
        },
        "stats": {
          "rentalMarket": "Appréciation Annuelle Moy.",
          "studioPrice": "Rendement Locatif Typique"
        }
      },
      "newsletter": {
        "title": "Restez Informé",
        "subtitle": "Recevez nos dernières opportunités d'investissement",
        "namePlaceholder": "Votre nom",
        "emailPlaceholder": "Votre email",
        "subscribe": "S'abonner",
        "submitting": "Envoi..."
      },
      "blog": {
        "readFullArticle": "Lire l'article complet",
        "loading": "Chargement de l'article...",
        "minRead": "min de lecture"
      },
      "blockchain": {
        "title": "Notre Expertise à Votre Service",
        "subtitle": "Israel Prime Estate combine une connaissance approfondie du marché, un réseau étendu et un service personnalisé pour vous offrir les meilleures opportunités immobilières en Israël.",
        "features": {
          "security": {
            "title": "Sécurité Totale",
            "description": "Diligence raisonnable juridique complète et vérification rigoureuse des propriétés pour garantir des transactions sûres et conformes"
          },
          "transparency": {
            "title": "Transparence Complète",
            "description": "Rapports détaillés sur chaque propriété : finances, état, statut juridique et analyse de marché"
          },
          "liquidity": {
            "title": "Réseau Exclusif",
            "description": "Accès privilégié aux opportunités hors marché et partenariats stratégiques avec les principaux acteurs du marché"
          }
        }
      },
      "privacy": {
        "pageTitle": "Politique de Confidentialité",
        "pageDescription": "Politique de Confidentialité d'Israel Prime Estate. Découvrez comment nous collectons, utilisons et protégeons vos informations personnelles.",
        "title": "Politique de Confidentialité",
        "introduction": {
          "title": "1. Introduction",
          "content": "Chez Israel Prime Estate, nous prenons votre vie privée au sérieux. Cette Politique de Confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations."
        },
        "informationCollection": {
          "title": "2. Informations que Nous Collectons",
          "intro": "Nous collectons divers types d'informations pour vous fournir le meilleur service possible :",
          "personal": {
            "title": "Informations Personnelles",
            "item1": "Nom complet et coordonnées",
            "item2": "Pièce d'identité émise par le gouvernement",
            "item3": "Date de naissance et nationalité",
            "item4": "Adresse résidentielle"
          },
          "financial": {
            "title": "Informations Financières",
            "item1": "Coordonnées bancaires",
            "item2": "Historique d'investissement",
            "item3": "Documentation sur l'origine des fonds"
          },
          "technical": {
            "title": "Informations Techniques",
            "item1": "Adresse IP et informations sur l'appareil",
            "item2": "Type et version du navigateur",
            "item3": "Modèles d'utilisation et préférences",
            "item4": "Cookies et données de suivi"
          }
        },
        "informationUse": {
          "title": "3. Comment Nous Utilisons Vos Informations",
          "intro": "Nous utilisons les informations collectées à diverses fins :",
          "account": {
            "title": "Gestion de Compte",
            "item1": "Création et maintenance de votre compte",
            "item2": "Vérification de votre identité",
            "item3": "Traitement des investissements et transactions",
            "item4": "Fourniture d'un support client"
          },
          "platform": {
            "title": "Amélioration de la Plateforme",
            "item1": "Analyse des modèles d'utilisation",
            "item2": "Développement de nouvelles fonctionnalités",
            "item3": "Amélioration de l'expérience utilisateur",
            "item4": "Résolution des problèmes techniques"
          },
          "communication": {
            "title": "Communication",
            "item1": "Envoi de confirmations de transaction",
            "item2": "Fourniture de mises à jour sur les investissements",
            "item3": "Communications marketing (avec consentement)",
            "item4": "Avis juridiques et réglementaires"
          }
        },
        "informationSharing": {
          "title": "4. Partage et Divulgation des Informations",
          "intro": "Nous pouvons partager vos informations avec :",
          "providers": {
            "title": "Prestataires de Services",
            "item1": "Processeurs de paiement",
            "item2": "Services de vérification d'identité",
            "item3": "Fournisseurs de stockage cloud",
            "item4": "Services d'analyse"
          },
          "legal": {
            "title": "Organismes Juridiques et Réglementaires",
            "item1": "Agences gouvernementales",
            "item2": "Autorités fiscales",
            "item3": "Forces de l'ordre",
            "item4": "Organismes de réglementation"
          }
        },
        "security": {
          "title": "5. Sécurité des Données",
          "intro": "Nous mettons en œuvre des mesures de sécurité robustes :",
          "item1": "Chiffrement des données sensibles",
          "item2": "Audits de sécurité réguliers",
          "item3": "Contrôles d'accès et authentification",
          "item4": "Stockage sécurisé des données",
          "item5": "Formation des employés à la protection des données",
          "item6": "Procédures de réponse aux incidents"
        },
        "rights": {
          "title": "6. Vos Droits et Choix",
          "intro": "Vous disposez de certains droits concernant vos informations personnelles :",
          "item1": "Accéder à vos informations personnelles",
          "item2": "Demander des corrections de données inexactes",
          "item3": "Demander la suppression de vos informations",
          "item4": "S'opposer au traitement de vos données",
          "item5": "Retirer votre consentement pour le marketing",
          "item6": "Droits à la portabilité des données",
          "item7": "Déposer des plaintes auprès des autorités de surveillance"
        },
        "international": {
          "title": "7. Transferts Internationaux de Données",
          "intro": "Vos informations peuvent être transférées et traitées dans différents pays :",
          "item1": "Normes de protection des données dans d'autres pays",
          "item2": "Garanties pour les transferts internationaux",
          "item3": "Conformité au Privacy Shield UE-US",
          "item4": "Clauses contractuelles types"
        },
        "children": {
          "title": "8. Vie Privée des Enfants",
          "content": "Notre plateforme n'est pas destinée aux personnes de moins de 18 ans. Nous ne collectons ni ne conservons sciemment d'informations sur les personnes de moins de 18 ans."
        },
        "updates": {
          "title": "9. Mises à Jour de Cette Politique",
          "content": "Nous pouvons mettre à jour cette politique de confidentialité de temps à autre. La version mise à jour sera indiquée par une date de « Révision » mise à jour."
        },
        "contact": {
          "title": "10. Informations de Contact",
          "content": "Si vous avez des questions sur cette Politique de Confidentialité, veuillez contacter notre Délégué à la Protection des Données à :",
          "email": "Email",
          "phone": "Téléphone",
          "address": "Adresse"
        }
      },
      "terms": {
        "pageTitle": "Conditions Générales",
        "pageDescription": "Conditions Générales d'Israel Prime Estate. Lisez nos conditions d'utilisation pour utiliser notre plateforme.",
        "title": "Conditions Générales",
        "subtitle": "Veuillez lire attentivement ces conditions générales avant d'utiliser nos services.",
        "acceptance": {
          "title": "Acceptation des Conditions",
          "description": "En accédant ou en utilisant la plateforme Israel Prime Estate, vous acceptez d'être lié par ces Conditions Générales."
        },
        "risks": {
          "title": "Risques d'Investissement",
          "description": "Les investissements immobiliers comportent des risques inhérents. Vous devez considérer attentivement :",
          "item1": "Les fluctuations du marché peuvent affecter la valeur des propriétés",
          "item2": "Les performances passées ne sont pas indicatives des résultats futurs",
          "item3": "Les investissements peuvent être illiquides pendant de longues périodes",
          "item4": "Les taux de change peuvent impacter les rendements",
          "item5": "Les changements réglementaires peuvent affecter les structures d'investissement"
        },
        "eligibility": {
          "title": "Conditions d'Éligibilité",
          "description": "Pour utiliser notre plateforme, vous devez satisfaire aux exigences suivantes :",
          "item1": "Avoir au moins 18 ans",
          "item2": "Avoir la capacité juridique de conclure des contrats",
          "item3": "Ne pas être résident d'une juridiction restreinte",
          "item4": "Compléter notre processus de vérification d'identité"
        },
        "account": {
          "title": "Sécurité du Compte",
          "description": "Vous êtes responsable du maintien de la sécurité de votre compte :",
          "item1": "Garder vos identifiants de connexion confidentiels",
          "item2": "Activer l'authentification à deux facteurs",
          "item3": "Signaler immédiatement tout accès non autorisé",
          "item4": "Examiner régulièrement l'activité de votre compte"
        },
        "investment": {
          "title": "Conditions d'Investissement",
          "description": "Lors d'investissements via notre plateforme :",
          "item1": "Tous les investissements sont soumis à disponibilité",
          "item2": "Des montants d'investissement minimum peuvent s'appliquer",
          "item3": "Les délais de traitement peuvent varier",
          "item4": "Les rendements ne sont pas garantis"
        },
        "usage": {
          "title": "Utilisation de la Plateforme",
          "description": "Les utilisateurs doivent respecter les directives suivantes :",
          "item1": "Utiliser la plateforme uniquement à des fins légales",
          "item2": "Ne pas tenter de contourner les mesures de sécurité",
          "item3": "Ne pas s'engager dans des activités frauduleuses",
          "item4": "Respecter les droits de propriété intellectuelle"
        },
        "fees": {
          "title": "Frais et Charges",
          "description": "Divers frais peuvent s'appliquer à nos services :",
          "item1": "Frais de gestion pour la supervision des propriétés",
          "item2": "Frais de transaction pour les investissements",
          "item3": "Frais de conversion de devises le cas échéant"
        },
        "intellectual": {
          "title": "Propriété Intellectuelle",
          "description": "Tout le contenu de la plateforme, y compris les textes, graphiques, logos et logiciels, est la propriété d'Israel Prime Estate et est protégé par les lois sur la propriété intellectuelle."
        },
        "privacySection": {
          "title": "Confidentialité",
          "description": "Votre utilisation de la plateforme est également régie par notre Politique de Confidentialité, qui décrit comment nous collectons, utilisons et protégeons vos informations personnelles."
        },
        "termination": {
          "title": "Résiliation",
          "description": "Nous nous réservons le droit de résilier ou de suspendre votre compte à tout moment pour violation de ces conditions ou pour toute autre raison que nous jugeons appropriée."
        },
        "governing": {
          "title": "Loi Applicable",
          "description": "Ces conditions sont régies par les lois d'Israël. Tout litige sera résolu devant les tribunaux de Tel Aviv, Israël."
        },
        "contact": {
          "title": "Informations de Contact",
          "intro": "Pour toute question concernant ces conditions, veuillez nous contacter à :",
          "email": "Email",
          "phone": "Téléphone",
          "address": "Adresse"
        }
      }
    }
  },
  he: {
    translation: {
      "nav": {
        "home": "בית",
        "properties": "נכסים",
        "about": "אודות",
        "contact": "צור קשר"
      },
      "hero": {
        "title": "הכתובות היוקרתיות ביותר בישראל",
        "tagline": "Israel Prime Estate",
        "subtitle": "אצירת המיוחד. אנו מספקים גישה דיסקרטית לנכסים מחוץ לשוק ושותפויות עם יזמים למשקיעים המתוחכמים ביותר בעולם.",
        "cta": {
          "discover": "גלה הזדמנויות",
          "contact": "צור קשר",
          "invest": "השקע"
        },
        "owners": "הם סומכים על המומחיות שלנו"
      },
      "whyNyref": {
        "title": "למה לבחור ב-Israel Prime Estate?",
        "subtitle": "הצטרף למאות משקיעים הסומכים על המומחיות שלנו ברכישות נדל\"ן בישראל",
        "features": {
          "portfolio": {
            "title": "נכסים שנבחרו בקפידה",
            "description": "גישה לבחירה בלעדית של נכסי פרימיום במיקומים האסטרטגיים ביותר בישראל."
          },
          "investment": {
            "title": "שירות ייעוץ מומחה",
            "description": "ליהנות מ-10 שנות ניסיון שלנו בשוק הישראלי. אנחנו מזהים את ההזדמנויות הטובות ביותר עבורך."
          },
          "income": {
            "title": "נכסים מוכנים לכניסה",
            "description": "רכוש נכסים שכבר מושכרים עם דיירים אמינים ותזרים מזומנים מיידי."
          },
          "market": {
            "title": "שוק ישראלי אסטרטגי",
            "description": "ישראל מציעה הזדמנויות ייחודיות עם ביקוש קבוע ותשואות אטרקטיביות. אנחנו עוזרים לך לנצל אותן."
          }
        }
      },
      "about": {
        "subtitle": "אנחנו לא מוכרים נכסים. אנחנו אוצרים נכסים אסטרטגיים.",
        "description": "נדל\"ן בישראל אינו רק עסקה; זוהי קטגוריית נכסים בפני עצמה. ב-Israel Prime Estate, אנו מגשרים בין חיי יוקרה לאינטליגנציה פיננסית. אנחנו לא רודפים אחרי השוק — אנחנו מגדירים אותו.",
        "description2": "באמצעות בריתות מועדפות עם היזמים המובילים במדינה, אנו מבטיחים נקודות כניסה שנשארות בלתי נראות לעין הציבור.",
        "partnerships": {
          "title": "גישה מחוץ לשוק",
          "description": "השוק הבלתי נראה — יוקרה אמיתית לעיתים רחוקות מוצגת לציבור. לקוחותינו מקבלים גישה עדיפה ל-40% מהמלאי הפרימיום שלעולם לא מגיע לפורטלים הציבוריים."
        },
        "financing": {
          "title": "ארכיטקטורת הון",
          "description": "מבנה בהתאמה אישית — אנו מתאימים עצמנו לצרכי הנזילות שלך. מאבני דרך מאוזנות ועד למבנים ממונפים בשיעור של עד 20/80, אנו מנהלים משא ומתן על תנאים המאפשרים לך לשלוט בנכס עם פריסת הון אופטימלית."
        },
        "selection": {
          "title": "מעמד \"השותף\"",
          "description": "התאמה אסטרטגית — אל תקנו בקמעונאות. שבו לשולחן היזמים. בין אם כרוכש מאובטח או כשותף בפיתוח משותף, אתם לוכדים ערך מתחילת הפרויקט."
        }
      },
      "legal": {
        "title": "מבנה משפטי ותהליך רכישה",
        "structure": {
          "title": "מבנה משפטי שקוף",
          "description": "כל נכס שאנחנו מציגים נהנה ממבנה משפטי ברור. אנחנו מלווים את לקוחותינו בכל שלב ברכישה, מבטיחים ביטחון משפטי והגנה על הנכסים."
        },
        "tokenization": {
          "title": "רכישה פשוטה",
          "description": "התהליך שלנו מפשט רכישת נדל\"ן בישראל. אנחנו מטפלים בכל המורכבויות המנהליות והמשפטיות כדי שתוכל להתמקד בהשקעה שלך."
        },
        "eligibility": {
          "title": "לקוחות בינלאומיים",
          "usInvestors": "כל הלאומיות",
          "international": "תמיכה מלאה"
        }
      },
      "advantages": {
        "title": "למה הלקוחות שלנו בוחרים בנו",
        "insider": {
          "title": "גישה פנימית",
          "description": "שותפויות ישירות עם יזמים נותנות לנו גישה לעסקאות מחוץ לשוק ומחירי טרום-השקה."
        },
        "savings": {
          "title": "חיסכון משמעותי",
          "description": "הלקוחות שלנו בדרך כלל חוסכים 15-30% בהשוואה למחירי השוק דרך הקשרים שלנו עם יזמים."
        },
        "service": {
          "title": "שירות כפפות לבנות",
          "description": "מהבחירה ועד לסגירה, אנחנו מטפלים בכל פרט. אתה מתמקד בהזדמנות."
        },
        "coInvestment": {
          "title": "השקעה משותפת מאובטחת",
          "description": "השקע לצד קונים מוסמכים אחרים עם הגנה משפטית של 100%. לא משנה מה החלק שלך, הבעלות שלך מאובטחת ורשומה במלואה."
        }
      },
      "faq": {
        "title": "שאלות נפוצות",
        "subtitle": "כל מה שצריך לדעת על הזדמנויות נדל\"ן בלעדיות בישראל"
      },
      "cta": {
        "title": "קבל גישה לעסקאות מחוץ לשוק",
        "subtitle": "גלה הזדמנויות בלעדיות שמורות ללקוחותינו.",
        "button": "קבע פגישת ייעוץ",
        "stayInformed": "הישאר מעודכן",
        "emailSignup": {
          "title": "קבל את הבחירה הבלעדית שלנו של נכסי פרימיום בישראל",
          "namePlaceholder": "הכנס את שמך",
          "emailPlaceholder": "הכנס את האימייל שלך",
          "subscribe": "הרשם"
        }
      },
      "footer": {
        "description": "השותף המהימן שלך לרכישת נדל\"ן פרימיום בישראל. הדרכה מומחית, הזדמנויות בלעדיות.",
        "support": "תמיכה",
        "contact": "צור קשר",
        "followUs": "עקבו אחרינו",
        "paymentMethods": "תמיכה ברכישה",
        "wireTransfer": "העברה בנקאית",
        "creditCards": "כל השיטות",
        "cryptoWallet": "בינלאומי",
        "copyright": "© 2025 IPE. כל הזכויות שמורות."
      },
      "contact": {
        "title": "בקר במשרד שלנו בתל אביב",
        "subtitle": "פגוש אותנו באופן אישי בלב שוק הנדל\"ן הישראלי",
        "visitUs": "המשרד שלנו",
        "address": {
          "title": "כתובת"
        },
        "phone": {
          "title": "טלפון"
        },
        "email": {
          "title": "אימייל"
        },
        "hours": {
          "title": "שעות פעילות"
        },
        "gallery": "המשרד שלנו בתל אביב",
        "form": {
          "title": "צור קשר",
          "subtitle": "קבע פגישת ייעוץ עם המומחים שלנו",
          "name": "השם שלך",
          "email": "כתובת אימייל",
          "phone": "מספר טלפון (אופציונלי)",
          "message": "ההודעה שלך",
          "submit": "שלח הודעה"
        }
      },
      "properties": {
        "title": "הבחירה הבלעדית שלנו של נכסי פרימיום",
        "subtitle": "גלה את תיק הנכסים שנבחרו בקפידה בישראל",
        "viewProperty": "צפה בפרטי הנכס",
        "comingSoon": "בקרוב",
        "tokenPrice": "מחיר ליחידה",
        "totalValue": "ערך הנכס",
        "availableTokens": "זמינות",
        "minInvestment": "החל מ",
        "neighbors": "מיקום פרימיום ליד",
        "units": "סה\"כ יחידות",
        "toBeAnnounced": "צור קשר",
        "yield": "תשואה צפויה",
        "israel": "ישראל"
      },
      "howItWorks": {
        "title": "איך תהליך הייעוץ שלנו עובד",
        "steps": {
          "step1": {
            "title": "גלה את הבחירה שלנו",
            "description": "עיין בתיק הבלעדי שלנו של נכסי פרימיום ישראליים, שנבחרו בקפידה על ידי המומחים שלנו."
          },
          "step2": {
            "title": "ייעוץ אישי",
            "description": "פגוש את היועצים שלנו שידריכו אותך בתהליך הרכישה ויענו על כל שאלותיך."
          },
          "step3": {
            "title": "רכישה מאובטחת",
            "description": "אנחנו מטפלים בכל ההיבטים המשפטיים והמנהליים של הרכישה כדי להבטיח עסקה חלקה ומאובטחת."
          },
          "step4": {
            "title": "תמיכה מתמשכת",
            "description": "ליהנות משירותי ניהול הנכסים שלנו ולמטב את ביצועי ההשקעה שלך."
          }
        }
      },
      "marketStats": {
        "title": "שוק הנדל\"ן הישראלי",
        "description": "שוק עמיד עם מסלול מוכח של הערכה לטווח ארוך",
        "stats": {
          "marketSize": "גודל השוק",
          "annualGrowth": "צמיחה שנתית",
          "avgReturn": "תשואה ממוצעת",
          "occupancyRate": "שיעור תפוסה"
        }
      },
      "tokenAdvantages": {
        "title": "היתרונות של שירות הייעוץ שלנו",
        "description": "ליהנות מהמומחיות והרשת הבלעדית שלנו בשוק הישראלי",
        "advantages": {
          "liquidity": {
            "title": "עלויות מותאמות",
            "description": "הגישה הישירה שלנו לבעלים והרשת הבלעדית מאפשרים לנו להציע לך את המחירים הטובים ביותר ולמזער עלויות עסקה."
          },
          "transparency": {
            "title": "תהליך רכישה מהיר",
            "description": "בזכות הניסיון והקשרים המבוססים שלנו, אנחנו מאיצים משמעותית את תהליך הרכישה תוך הבטחת ביטחון מלא."
          },
          "accessibility": {
            "title": "תמיכה משפטית מלאה",
            "description": "הצוות המשפטי שלנו מטפל בכל היבטי העסקה, מבדיקת נאותות ועד העברת בעלות, מבטיח הגנה מיטבית על הנכסים."
          },
          "flexibility": {
            "title": "פתרונות גמישים",
            "description": "בין אם אתה מחפש נכסים מוכנים לכניסה או הזדמנויות פיתוח, אנחנו מתאימים את השירותים שלנו ליעדי ההשקעה הספציפיים שלך."
          },
          "digitalManagement": {
            "title": "ניהול נכסים מקצועי",
            "description": "ליהנות משירותי ניהול הנכסים שלנו: בחירת דיירים, גביית שכ\"ד, תחזוקה. שקט נפשי מלא לתשואות מיטביות."
          },
          "paperless": {
            "title": "תהליך פשוט",
            "description": "אנחנו מטפלים בכל המורכבות המנהלית. התמקד בהשקעה שלך בזמן שאנחנו מטפלים בתיעוד ובהליכים."
          }
        },
        "stats": {
          "processing": "הדרכה מומחית",
          "costReduction": "עלויות מותאמות",
          "access": "גישה בלעדית"
        }
      },
      "press": {
        "title": "Israel Prime Estate בתקשורת",
        "subtitle": "מה אומרים עלינו בתקשורת"
      },
      "events": {
        "title": "אירועים קרובים",
        "subtitle": "הצטרף לאירועים הבלעדיים שלנו"
      },
      "israelRealEstate": {
        "title": "למה להשקיע בנדל\"ן ישראלי עכשיו?",
        "features": {
          "valuableMarket": {
            "title": "שוק חזק וצומח",
            "description": "שוק הנדל\"ן הישראלי סיפק הערכה שנתית עקבית של 5-8%. עם כלכלה חזקה, מעמד של מרכז טכנולוגי (\"עמק הסיליקון\") והיצע מוגבל באזורים מבוקשים, הערכים ממשיכים לעלות."
          },
          "democratizing": {
            "title": "יתרון מטבע חזק",
            "description": "השקל הישראלי התחזק ביותר מ-25% מול מטבעות עיקריים בעשור האחרון. ההשקעה שלך נהנית גם מהערכת הנכס וגם מחוזק המטבע."
          },
          "trillion": {
            "title": "תשואות שכירות גבוהות",
            "description": "נכסים ישראליים מציעים תשואות שכירות אטרקטיביות (4-6%) עם ביקוש חזק לדיירים. היצע מוגבל פוגש ביקוש קבוע מתושבים מקומיים, מהתפוצות ומאנשי מקצוע בינלאומיים."
          },
          "ownIsrael": {
            "title": "החזק את החלק שלך בישראל",
            "description": "מעבר לתשואות, יש חיבור רגשי. בנה את המורשת שלך וחזק את הקשרים שלך לישראל דרך בעלות מוחשית על נכס."
          }
        },
        "stats": {
          "rentalMarket": "הערכה שנתית ממוצעת",
          "studioPrice": "תשואת שכירות טיפוסית"
        }
      },
      "newsletter": {
        "title": "הישאר מעודכן",
        "subtitle": "קבל את הזדמנויות ההשקעה האחרונות שלנו",
        "namePlaceholder": "השם שלך",
        "emailPlaceholder": "האימייל שלך",
        "subscribe": "הרשם",
        "submitting": "שולח..."
      },
      "blog": {
        "readFullArticle": "קרא את הכתבה המלאה",
        "loading": "טוען כתבה...",
        "minRead": "דק' קריאה"
      },
      "blockchain": {
        "title": "המומחיות שלנו לשירותך",
        "subtitle": "Israel Prime Estate משלב ידע עמוק בשוק, רשת רחבה ושירות אישי כדי להציע לך את הזדמנויות הנדל\"ן הטובות ביותר בישראל.",
        "features": {
          "security": {
            "title": "ביטחון מלא",
            "description": "בדיקת נאותות משפטית מלאה ואימות קפדני של נכסים להבטחת עסקאות בטוחות ותואמות"
          },
          "transparency": {
            "title": "שקיפות מלאה",
            "description": "דוחות מפורטים על כל נכס: פיננסים, מצב, סטטוס משפטי וניתוח שוק"
          },
          "liquidity": {
            "title": "רשת בלעדית",
            "description": "גישה מועדפת להזדמנויות מחוץ לשוק ושותפויות אסטרטגיות עם שחקנים מרכזיים בשוק"
          }
        }
      },
      "privacy": {
        "pageTitle": "מדיניות פרטיות",
        "pageDescription": "מדיניות הפרטיות של Israel Prime Estate. למד כיצד אנו אוספים, משתמשים ומגנים על המידע האישי שלך.",
        "title": "מדיניות פרטיות",
        "introduction": {
          "title": "1. מבוא",
          "content": "ב-Israel Prime Estate אנו מתייחסים לפרטיות שלך ברצינות. מדיניות פרטיות זו מסבירה כיצד אנו אוספים, משתמשים, חושפים ומגנים על המידע שלך כאשר אתה משתמש בפלטפורמה שלנו."
        },
        "informationCollection": {
          "title": "2. מידע שאנו אוספים",
          "intro": "אנו אוספים סוגים שונים של מידע כדי לספק לך את השירות הטוב ביותר:",
          "personal": {
            "title": "מידע אישי",
            "item1": "שם מלא ופרטי קשר",
            "item2": "תעודה מזהה ממשלתית",
            "item3": "תאריך לידה ואזרחות",
            "item4": "כתובת מגורים"
          },
          "financial": {
            "title": "מידע פיננסי",
            "item1": "פרטי חשבון בנק",
            "item2": "היסטוריית השקעות",
            "item3": "תיעוד מקור כספים"
          },
          "technical": {
            "title": "מידע טכני",
            "item1": "כתובת IP ומידע על המכשיר",
            "item2": "סוג וגרסת דפדפן",
            "item3": "דפוסי שימוש והעדפות",
            "item4": "עוגיות ונתוני מעקב"
          }
        },
        "informationUse": {
          "title": "3. כיצד אנו משתמשים במידע שלך",
          "intro": "אנו משתמשים במידע שנאסף למטרות שונות:",
          "account": {
            "title": "ניהול חשבון",
            "item1": "יצירה ותחזוקה של החשבון שלך",
            "item2": "אימות הזהות שלך",
            "item3": "עיבוד השקעות ועסקאות",
            "item4": "מתן תמיכת לקוחות"
          },
          "platform": {
            "title": "שיפור הפלטפורמה",
            "item1": "ניתוח דפוסי שימוש",
            "item2": "פיתוח תכונות חדשות",
            "item3": "שיפור חוויית המשתמש",
            "item4": "פתרון בעיות טכניות"
          },
          "communication": {
            "title": "תקשורת",
            "item1": "שליחת אישורי עסקאות",
            "item2": "מתן עדכוני השקעות",
            "item3": "תקשורת שיווקית (בהסכמה)",
            "item4": "הודעות משפטיות ורגולטוריות"
          }
        },
        "informationSharing": {
          "title": "4. שיתוף וחשיפת מידע",
          "intro": "אנו עשויים לשתף את המידע שלך עם:",
          "providers": {
            "title": "ספקי שירות",
            "item1": "מעבדי תשלומים",
            "item2": "שירותי אימות זהות",
            "item3": "ספקי אחסון ענן",
            "item4": "שירותי ניתוח"
          },
          "legal": {
            "title": "גופים משפטיים ורגולטוריים",
            "item1": "סוכנויות ממשלתיות",
            "item2": "רשויות מס",
            "item3": "רשויות אכיפת חוק",
            "item4": "גופים רגולטוריים"
          }
        },
        "security": {
          "title": "5. אבטחת מידע",
          "intro": "אנו מיישמים אמצעי אבטחה חזקים:",
          "item1": "הצפנה של נתונים רגישים",
          "item2": "ביקורות אבטחה קבועות",
          "item3": "בקרות גישה ואימות",
          "item4": "אחסון נתונים מאובטח",
          "item5": "הכשרת עובדים בהגנת נתונים",
          "item6": "נהלי תגובה לאירועים"
        },
        "rights": {
          "title": "6. הזכויות והבחירות שלך",
          "intro": "יש לך זכויות מסוימות לגבי המידע האישי שלך:",
          "item1": "גישה למידע האישי שלך",
          "item2": "בקשה לתיקון נתונים לא מדויקים",
          "item3": "בקשה למחיקת המידע שלך",
          "item4": "התנגדות לעיבוד הנתונים שלך",
          "item5": "משיכת הסכמה לשיווק",
          "item6": "זכויות ניידות נתונים",
          "item7": "הגשת תלונות לרשויות פיקוח"
        },
        "international": {
          "title": "7. העברות נתונים בינלאומיות",
          "intro": "המידע שלך עשוי להיות מועבר ומעובד במדינות שונות:",
          "item1": "תקני הגנת נתונים במדינות אחרות",
          "item2": "הגנות להעברות בינלאומיות",
          "item3": "עמידה ב-Privacy Shield אירופה-ארה\"ב",
          "item4": "סעיפים חוזיים סטנדרטיים"
        },
        "children": {
          "title": "8. פרטיות ילדים",
          "content": "הפלטפורמה שלנו אינה מיועדת לאנשים מתחת לגיל 18. אנחנו לא אוספים או מחזיקים ביודעין מידע מאנשים מתחת לגיל 18."
        },
        "updates": {
          "title": "9. עדכונים למדיניות זו",
          "content": "אנו עשויים לעדכן מדיניות פרטיות זו מעת לעת. הגרסה המעודכנת תסומן בתאריך \"עודכן\" מעודכן."
        },
        "contact": {
          "title": "10. פרטי קשר",
          "content": "אם יש לך שאלות על מדיניות פרטיות זו, אנא צור קשר עם קצין הגנת הנתונים שלנו ב:",
          "email": "אימייל",
          "phone": "טלפון",
          "address": "כתובת"
        }
      },
      "terms": {
        "pageTitle": "תנאים והגבלות",
        "pageDescription": "תנאים והגבלות של Israel Prime Estate. קרא את תנאי השירות שלנו לשימוש בפלטפורמה.",
        "title": "תנאים והגבלות",
        "subtitle": "אנא קרא תנאים והגבלות אלה בקפידה לפני השימוש בשירותינו.",
        "acceptance": {
          "title": "קבלת התנאים",
          "description": "על ידי גישה או שימוש בפלטפורמת Israel Prime Estate, אתה מסכים להיות מחויב לתנאים והגבלות אלה."
        },
        "risks": {
          "title": "סיכוני השקעה",
          "description": "השקעות נדל\"ן כרוכות בסיכונים מובנים. עליך לשקול בקפידה:",
          "item1": "תנודות שוק עשויות להשפיע על ערכי הנכסים",
          "item2": "ביצועי עבר אינם מעידים על תוצאות עתידיות",
          "item3": "השקעות עשויות להיות לא נזילות לתקופות ממושכות",
          "item4": "שערי חליפין עשויים להשפיע על התשואות",
          "item5": "שינויים רגולטוריים עשויים להשפיע על מבני ההשקעה"
        },
        "eligibility": {
          "title": "דרישות זכאות",
          "description": "כדי להשתמש בפלטפורמה שלנו, עליך לעמוד בדרישות הבאות:",
          "item1": "להיות בן 18 לפחות",
          "item2": "להיות בעל כשירות משפטית לכריתת חוזים",
          "item3": "לא להיות תושב של תחומי שיפוט מוגבלים",
          "item4": "להשלים את תהליך אימות הזהות שלנו"
        },
        "account": {
          "title": "אבטחת חשבון",
          "description": "אתה אחראי לשמירה על אבטחת החשבון שלך:",
          "item1": "שמור על פרטי הכניסה שלך בסודיות",
          "item2": "הפעל אימות דו-שלבי",
          "item3": "דווח מיד על גישה בלתי מורשית",
          "item4": "בדוק באופן קבוע את פעילות החשבון שלך"
        },
        "investment": {
          "title": "תנאי השקעה",
          "description": "בעת ביצוע השקעות דרך הפלטפורמה שלנו:",
          "item1": "כל ההשקעות כפופות לזמינות",
          "item2": "עשויים לחול סכומי השקעה מינימליים",
          "item3": "זמני עיבוד עשויים להשתנות",
          "item4": "תשואות אינן מובטחות"
        },
        "usage": {
          "title": "שימוש בפלטפורמה",
          "description": "המשתמשים חייבים לציית להנחיות הבאות:",
          "item1": "להשתמש בפלטפורמה רק למטרות חוקיות",
          "item2": "לא לנסות לעקוף אמצעי אבטחה",
          "item3": "לא לעסוק בפעילויות הונאה",
          "item4": "לכבד זכויות קניין רוחני"
        },
        "fees": {
          "title": "עמלות וחיובים",
          "description": "עמלות שונות עשויות לחול על השירותים שלנו:",
          "item1": "דמי ניהול לפיקוח על נכסים",
          "item2": "עמלות עסקה להשקעות",
          "item3": "עמלות המרת מטבע כאשר ישים"
        },
        "intellectual": {
          "title": "קניין רוחני",
          "description": "כל התוכן בפלטפורמה, כולל טקסט, גרפיקה, לוגואים ותוכנה, הוא רכוש Israel Prime Estate ומוגן על ידי חוקי קניין רוחני."
        },
        "privacySection": {
          "title": "פרטיות",
          "description": "השימוש שלך בפלטפורמה כפוף גם למדיניות הפרטיות שלנו, המתארת כיצד אנו אוספים, משתמשים ומגנים על המידע האישי שלך."
        },
        "termination": {
          "title": "סיום",
          "description": "אנו שומרים לעצמנו את הזכות לסיים או להשעות את החשבון שלך בכל עת בגין הפרה של תנאים אלה או מכל סיבה אחרת שאנו רואים לנכון."
        },
        "governing": {
          "title": "דין חל",
          "description": "תנאים אלה כפופים לחוקי ישראל. כל מחלוקת תיפתר בבתי המשפט בתל אביב, ישראל."
        },
        "contact": {
          "title": "פרטי קשר",
          "intro": "לשאלות בנוגע לתנאים אלה, אנא צור איתנו קשר ב:",
          "email": "אימייל",
          "phone": "טלפון",
          "address": "כתובת"
        }
      }
    }
  },
  ru: {
    translation: {
      "nav": {
        "home": "Главная",
        "properties": "Недвижимость",
        "about": "О нас",
        "contact": "Контакты"
      },
      "hero": {
        "title": "Самые престижные адреса Израиля",
        "tagline": "Israel Prime Estate",
        "subtitle": "Курирование исключительного. Мы предоставляем конфиденциальный доступ к недвижимости вне рынка и партнёрствам с застройщиками для самых искушённых инвесторов мира.",
        "cta": {
          "discover": "Открыть возможности",
          "contact": "Связаться с нами",
          "invest": "Инвестировать"
        },
        "owners": "Они доверяют нашей экспертизе"
      },
      "whyNyref": {
        "title": "Почему выбирают Israel Prime Estate?",
        "subtitle": "Присоединяйтесь к сотням инвесторов, которые доверяют нашей экспертизе при приобретении недвижимости в Израиле",
        "features": {
          "portfolio": {
            "title": "Тщательно отобранная недвижимость",
            "description": "Доступ к эксклюзивной подборке премиальной недвижимости в самых стратегических локациях Израиля."
          },
          "investment": {
            "title": "Экспертные консультации",
            "description": "Воспользуйтесь нашим 10-летним опытом работы на израильском рынке. Мы находим лучшие возможности для вас."
          },
          "income": {
            "title": "Готовая к заселению недвижимость",
            "description": "Приобретайте недвижимость с надёжными арендаторами и немедленным денежным потоком."
          },
          "market": {
            "title": "Стратегический израильский рынок",
            "description": "Израиль предлагает уникальные возможности с постоянным спросом и привлекательной доходностью. Мы поможем вам воспользоваться ими."
          }
        }
      },
      "about": {
        "subtitle": "Мы не продаём недвижимость. Мы курируем стратегические активы.",
        "description": "Недвижимость в Израиле — это не просто сделка; это отдельный класс активов. В Israel Prime Estate мы создаём мост между роскошной жизнью и финансовой стратегией. Мы не следуем за рынком — мы его определяем.",
        "description2": "Благодаря привилегированным альянсам с ведущими застройщиками страны, мы обеспечиваем точки входа, недоступные широкой публике.",
        "partnerships": {
          "title": "Доступ вне рынка",
          "description": "Невидимый рынок — Истинная роскошь редко выставляется на продажу. Наши клиенты получают приоритетный доступ к 40% премиального инвентаря, который никогда не попадает на публичные порталы."
        },
        "financing": {
          "title": "Архитектура капитала",
          "description": "Индивидуальное структурирование — Мы адаптируемся к вашим потребностям в ликвидности. От сбалансированных этапов до структур с высоким левериджем до 20/80, мы согласовываем условия, которые позволяют вам контролировать актив с оптимизированным размещением капитала."
        },
        "selection": {
          "title": "Статус \"Партнёр\"",
          "description": "Стратегическое выравнивание — Не покупайте в розницу. Садитесь за стол застройщика. Будь то надёжный покупатель или партнёр по совместному развитию, вы получаете ценность с самого начала проекта."
        }
      },
      "legal": {
        "title": "Юридическая структура и процесс приобретения",
        "structure": {
          "title": "Прозрачная юридическая структура",
          "description": "Каждый объект, который мы представляем, имеет чёткую юридическую структуру. Мы сопровождаем наших клиентов на каждом этапе приобретения, обеспечивая юридическую безопасность и защиту активов."
        },
        "tokenization": {
          "title": "Упрощённое приобретение",
          "description": "Наш процесс упрощает приобретение недвижимости в Израиле. Мы берём на себя все административные и юридические сложности, чтобы вы могли сосредоточиться на инвестициях."
        },
        "eligibility": {
          "title": "Международные клиенты",
          "usInvestors": "Все национальности",
          "international": "Полное сопровождение"
        }
      },
      "advantages": {
        "title": "Почему наши клиенты выбирают нас",
        "insider": {
          "title": "Инсайдерский доступ",
          "description": "Прямые партнёрства с застройщиками дают нам доступ к сделкам вне рынка и ценам до запуска."
        },
        "savings": {
          "title": "Значительная экономия",
          "description": "Наши клиенты обычно экономят 15-30% по сравнению с рыночными ценами благодаря нашим отношениям с застройщиками."
        },
        "service": {
          "title": "Премиум-сервис",
          "description": "От выбора до закрытия сделки мы берём на себя каждую деталь. Вы фокусируетесь на возможности."
        },
        "coInvestment": {
          "title": "Безопасные совместные инвестиции",
          "description": "Инвестируйте вместе с другими квалифицированными покупателями со 100% юридической защитой. Какова бы ни была ваша доля, ваша собственность полностью защищена и зарегистрирована."
        }
      },
      "faq": {
        "title": "Часто задаваемые вопросы",
        "subtitle": "Всё, что вам нужно знать об эксклюзивных возможностях недвижимости в Израиле"
      },
      "cta": {
        "title": "Получите доступ к внерыночным предложениям",
        "subtitle": "Откройте эксклюзивные возможности, зарезервированные для наших клиентов.",
        "button": "Запланировать консультацию",
        "stayInformed": "Будьте в курсе",
        "emailSignup": {
          "title": "Получайте нашу эксклюзивную подборку премиальной недвижимости в Израиле",
          "namePlaceholder": "Введите ваше имя",
          "emailPlaceholder": "Введите ваш email",
          "subscribe": "Подписаться"
        }
      },
      "footer": {
        "description": "Ваш надёжный партнёр для приобретения премиальной недвижимости в Израиле. Экспертное сопровождение, эксклюзивные возможности.",
        "support": "Поддержка",
        "contact": "Контакты",
        "followUs": "Следите за нами",
        "paymentMethods": "Поддержка приобретения",
        "wireTransfer": "Банковский перевод",
        "creditCards": "Все способы",
        "cryptoWallet": "Международный",
        "copyright": "© 2025 IPE. Все права защищены."
      },
      "contact": {
        "title": "Посетите наш офис в Тель-Авиве",
        "subtitle": "Встретьтесь с нами лично в сердце израильского рынка недвижимости",
        "visitUs": "Наш офис",
        "address": {
          "title": "Адрес"
        },
        "phone": {
          "title": "Телефон"
        },
        "email": {
          "title": "Email"
        },
        "hours": {
          "title": "Часы работы"
        },
        "gallery": "Наш офис в Тель-Авиве",
        "form": {
          "title": "Связаться с нами",
          "subtitle": "Запланируйте консультацию с нашими экспертами",
          "name": "Ваше имя",
          "email": "Email адрес",
          "phone": "Номер телефона (опционально)",
          "message": "Ваше сообщение",
          "submit": "Отправить сообщение"
        }
      },
      "properties": {
        "title": "Наша эксклюзивная подборка премиальной недвижимости",
        "subtitle": "Откройте тщательно отобранное портфолио израильской недвижимости",
        "viewProperty": "Посмотреть детали объекта",
        "comingSoon": "Скоро",
        "tokenPrice": "Цена за единицу",
        "totalValue": "Стоимость объекта",
        "availableTokens": "Доступность",
        "minInvestment": "От",
        "neighbors": "Премиум-локация рядом с",
        "units": "Всего квартир",
        "toBeAnnounced": "Свяжитесь с нами",
        "yield": "Ожидаемая доходность",
        "israel": "Израиль"
      },
      "howItWorks": {
        "title": "Как работает наш консультационный процесс",
        "steps": {
          "step1": {
            "title": "Изучите нашу подборку",
            "description": "Просмотрите наше эксклюзивное портфолио премиальной израильской недвижимости, тщательно отобранной нашими экспертами."
          },
          "step2": {
            "title": "Персональная консультация",
            "description": "Встретьтесь с нашими консультантами, которые проведут вас через процесс приобретения и ответят на все ваши вопросы."
          },
          "step3": {
            "title": "Безопасное приобретение",
            "description": "Мы берём на себя все юридические и административные аспекты покупки для обеспечения гладкой и безопасной сделки."
          },
          "step4": {
            "title": "Постоянная поддержка",
            "description": "Воспользуйтесь нашими услугами по управлению недвижимостью и оптимизируйте доходность ваших инвестиций."
          }
        }
      },
      "marketStats": {
        "title": "Израильский рынок недвижимости",
        "description": "Устойчивый рынок с проверенной историей долгосрочного роста",
        "stats": {
          "marketSize": "Размер рынка",
          "annualGrowth": "Годовой рост",
          "avgReturn": "Средняя доходность",
          "occupancyRate": "Заполняемость"
        }
      },
      "tokenAdvantages": {
        "title": "Преимущества нашего консультационного сервиса",
        "description": "Воспользуйтесь нашей экспертизой и эксклюзивной сетью на израильском рынке",
        "advantages": {
          "liquidity": {
            "title": "Оптимизированные расходы",
            "description": "Наш прямой доступ к владельцам и эксклюзивная сеть позволяют нам предложить вам лучшие цены и минимизировать транзакционные расходы."
          },
          "transparency": {
            "title": "Быстрый процесс приобретения",
            "description": "Благодаря нашему опыту и устоявшимся связям мы значительно ускоряем процесс приобретения, обеспечивая полную безопасность."
          },
          "accessibility": {
            "title": "Полная юридическая поддержка",
            "description": "Наша юридическая команда занимается всеми аспектами сделки, от проверки до передачи права собственности, обеспечивая оптимальную защиту активов."
          },
          "flexibility": {
            "title": "Гибкие решения",
            "description": "Ищете ли вы готовую к заселению недвижимость или возможности для развития, мы адаптируем наши услуги к вашим конкретным инвестиционным целям."
          },
          "digitalManagement": {
            "title": "Профессиональное управление недвижимостью",
            "description": "Воспользуйтесь нашими услугами по управлению: подбор арендаторов, сбор арендной платы, обслуживание. Полное спокойствие для оптимальной доходности."
          },
          "paperless": {
            "title": "Упрощённый процесс",
            "description": "Мы берём на себя всю административную сложность. Сосредоточьтесь на инвестициях, пока мы занимаемся документацией и процедурами."
          }
        },
        "stats": {
          "processing": "Экспертное сопровождение",
          "costReduction": "Оптимизированные расходы",
          "access": "Эксклюзивный доступ"
        }
      },
      "press": {
        "title": "Israel Prime Estate в прессе",
        "subtitle": "Что СМИ говорят о нас"
      },
      "events": {
        "title": "Предстоящие события",
        "subtitle": "Присоединяйтесь к нашим эксклюзивным мероприятиям"
      },
      "israelRealEstate": {
        "title": "Почему инвестировать в израильскую недвижимость сейчас?",
        "features": {
          "valuableMarket": {
            "title": "Сильный и растущий рынок",
            "description": "Израильский рынок недвижимости стабильно показывает 5-8% годового прироста. С устойчивой экономикой, статусом технологического хаба (\"Кремниевая долина\") и ограниченным предложением в премиальных районах, стоимость продолжает расти."
          },
          "democratizing": {
            "title": "Преимущество сильной валюты",
            "description": "Израильский шекель (ILS) укрепился более чем на 25% по отношению к основным валютам за последнее десятилетие. Ваши инвестиции выигрывают и от роста стоимости недвижимости, и от силы валюты."
          },
          "trillion": {
            "title": "Высокая арендная доходность",
            "description": "Израильская недвижимость предлагает привлекательную арендную доходность (4-6%) при сильном спросе арендаторов. Ограниченное предложение встречает постоянный спрос от местных жителей, диаспоры и международных специалистов."
          },
          "ownIsrael": {
            "title": "Владейте частью Израиля",
            "description": "Помимо доходности, есть эмоциональная связь. Создайте своё наследие и укрепите связи с Израилем через владение реальной недвижимостью."
          }
        },
        "stats": {
          "rentalMarket": "Среднегодовой прирост",
          "studioPrice": "Типичная арендная доходность"
        }
      },
      "newsletter": {
        "title": "Будьте в курсе",
        "subtitle": "Получайте наши последние инвестиционные возможности",
        "namePlaceholder": "Ваше имя",
        "emailPlaceholder": "Ваш email",
        "subscribe": "Подписаться",
        "submitting": "Отправка..."
      },
      "blog": {
        "readFullArticle": "Читать полную статью",
        "loading": "Загрузка статьи...",
        "minRead": "мин чтения"
      },
      "blockchain": {
        "title": "Наша экспертиза к вашим услугам",
        "subtitle": "Israel Prime Estate сочетает глубокое знание рынка, обширную сеть и персонализированный сервис, чтобы предложить вам лучшие возможности недвижимости в Израиле.",
        "features": {
          "security": {
            "title": "Полная безопасность",
            "description": "Комплексная юридическая проверка и тщательная верификация объектов для обеспечения безопасных и соответствующих транзакций"
          },
          "transparency": {
            "title": "Полная прозрачность",
            "description": "Детальные отчёты по каждому объекту: финансы, состояние, юридический статус и анализ рынка"
          },
          "liquidity": {
            "title": "Эксклюзивная сеть",
            "description": "Привилегированный доступ к внерыночным возможностям и стратегические партнёрства с ключевыми игроками рынка"
          }
        }
      },
      "privacy": {
        "pageTitle": "Политика конфиденциальности",
        "pageDescription": "Политика конфиденциальности Israel Prime Estate. Узнайте, как мы собираем, используем и защищаем вашу личную информацию.",
        "title": "Политика конфиденциальности",
        "introduction": {
          "title": "1. Введение",
          "content": "В Israel Prime Estate мы серьёзно относимся к вашей конфиденциальности. Эта Политика конфиденциальности объясняет, как мы собираем, используем, раскрываем и защищаем вашу информацию при использовании нашей платформы."
        },
        "informationCollection": {
          "title": "2. Информация, которую мы собираем",
          "intro": "Мы собираем различные типы информации для предоставления вам наилучшего сервиса:",
          "personal": {
            "title": "Личная информация",
            "item1": "Полное имя и контактные данные",
            "item2": "Государственный документ, удостоверяющий личность",
            "item3": "Дата рождения и гражданство",
            "item4": "Адрес проживания"
          },
          "financial": {
            "title": "Финансовая информация",
            "item1": "Реквизиты банковского счёта",
            "item2": "История инвестиций",
            "item3": "Документы о происхождении средств"
          },
          "technical": {
            "title": "Техническая информация",
            "item1": "IP-адрес и информация об устройстве",
            "item2": "Тип и версия браузера",
            "item3": "Паттерны использования и предпочтения",
            "item4": "Cookies и данные отслеживания"
          }
        },
        "informationUse": {
          "title": "3. Как мы используем вашу информацию",
          "intro": "Мы используем собранную информацию в различных целях:",
          "account": {
            "title": "Управление аккаунтом",
            "item1": "Создание и поддержание вашего аккаунта",
            "item2": "Верификация вашей личности",
            "item3": "Обработка инвестиций и транзакций",
            "item4": "Поддержка клиентов"
          },
          "platform": {
            "title": "Улучшение платформы",
            "item1": "Анализ паттернов использования",
            "item2": "Разработка новых функций",
            "item3": "Улучшение пользовательского опыта",
            "item4": "Устранение технических проблем"
          },
          "communication": {
            "title": "Коммуникация",
            "item1": "Отправка подтверждений транзакций",
            "item2": "Предоставление инвестиционных обновлений",
            "item3": "Маркетинговые сообщения (с согласия)",
            "item4": "Юридические и регуляторные уведомления"
          }
        },
        "informationSharing": {
          "title": "4. Передача и раскрытие информации",
          "intro": "Мы можем передавать вашу информацию:",
          "providers": {
            "title": "Поставщикам услуг",
            "item1": "Платёжные процессоры",
            "item2": "Сервисы верификации личности",
            "item3": "Провайдеры облачного хранения",
            "item4": "Аналитические сервисы"
          },
          "legal": {
            "title": "Юридическим и регуляторным органам",
            "item1": "Государственные органы",
            "item2": "Налоговые органы",
            "item3": "Правоохранительные органы",
            "item4": "Регуляторные органы"
          }
        },
        "security": {
          "title": "5. Безопасность данных",
          "intro": "Мы внедряем надёжные меры безопасности:",
          "item1": "Шифрование конфиденциальных данных",
          "item2": "Регулярные аудиты безопасности",
          "item3": "Контроль доступа и аутентификация",
          "item4": "Безопасное хранение данных",
          "item5": "Обучение сотрудников защите данных",
          "item6": "Процедуры реагирования на инциденты"
        },
        "rights": {
          "title": "6. Ваши права и выбор",
          "intro": "Вы имеете определённые права в отношении вашей личной информации:",
          "item1": "Доступ к вашей личной информации",
          "item2": "Запрос на исправление неточных данных",
          "item3": "Запрос на удаление вашей информации",
          "item4": "Возражение против обработки ваших данных",
          "item5": "Отзыв согласия на маркетинг",
          "item6": "Права на переносимость данных",
          "item7": "Подача жалоб в надзорные органы"
        },
        "international": {
          "title": "7. Международная передача данных",
          "intro": "Ваша информация может передаваться и обрабатываться в разных странах:",
          "item1": "Стандарты защиты данных в других странах",
          "item2": "Меры защиты для международных передач",
          "item3": "Соответствие Privacy Shield ЕС-США",
          "item4": "Стандартные договорные положения"
        },
        "children": {
          "title": "8. Конфиденциальность детей",
          "content": "Наша платформа не предназначена для лиц младше 18 лет. Мы сознательно не собираем и не храним информацию о лицах младше 18 лет."
        },
        "updates": {
          "title": "9. Обновления политики",
          "content": "Мы можем время от времени обновлять эту политику конфиденциальности. Обновлённая версия будет обозначена обновлённой датой \"Редакция\"."
        },
        "contact": {
          "title": "10. Контактная информация",
          "content": "Если у вас есть вопросы по этой Политике конфиденциальности, пожалуйста, свяжитесь с нашим Офицером по защите данных:",
          "email": "Email",
          "phone": "Телефон",
          "address": "Адрес"
        }
      },
      "terms": {
        "pageTitle": "Условия и положения",
        "pageDescription": "Условия и положения Israel Prime Estate. Прочитайте наши условия использования платформы.",
        "title": "Условия и положения",
        "subtitle": "Пожалуйста, внимательно прочитайте эти условия и положения перед использованием наших услуг.",
        "acceptance": {
          "title": "Принятие условий",
          "description": "Получая доступ или используя платформу Israel Prime Estate, вы соглашаетесь соблюдать эти Условия и положения."
        },
        "risks": {
          "title": "Инвестиционные риски",
          "description": "Инвестиции в недвижимость несут присущие риски. Вам следует внимательно рассмотреть:",
          "item1": "Рыночные колебания могут влиять на стоимость недвижимости",
          "item2": "Прошлые результаты не гарантируют будущих результатов",
          "item3": "Инвестиции могут быть неликвидными в течение длительных периодов",
          "item4": "Курсы валют могут влиять на доходность",
          "item5": "Регуляторные изменения могут влиять на структуры инвестиций"
        },
        "eligibility": {
          "title": "Требования к участникам",
          "description": "Для использования нашей платформы вы должны соответствовать следующим требованиям:",
          "item1": "Быть не моложе 18 лет",
          "item2": "Обладать правоспособностью для заключения договоров",
          "item3": "Не быть резидентом ограниченных юрисдикций",
          "item4": "Пройти наш процесс верификации личности"
        },
        "account": {
          "title": "Безопасность аккаунта",
          "description": "Вы несёте ответственность за поддержание безопасности вашего аккаунта:",
          "item1": "Храните ваши учётные данные в тайне",
          "item2": "Включите двухфакторную аутентификацию",
          "item3": "Немедленно сообщайте о несанкционированном доступе",
          "item4": "Регулярно проверяйте активность аккаунта"
        },
        "investment": {
          "title": "Условия инвестирования",
          "description": "При инвестировании через нашу платформу:",
          "item1": "Все инвестиции зависят от наличия",
          "item2": "Могут применяться минимальные суммы инвестиций",
          "item3": "Сроки обработки могут варьироваться",
          "item4": "Доходность не гарантируется"
        },
        "usage": {
          "title": "Использование платформы",
          "description": "Пользователи должны соблюдать следующие правила:",
          "item1": "Использовать платформу только в законных целях",
          "item2": "Не пытаться обойти меры безопасности",
          "item3": "Не заниматься мошеннической деятельностью",
          "item4": "Уважать права интеллектуальной собственности"
        },
        "fees": {
          "title": "Сборы и платежи",
          "description": "К нашим услугам могут применяться различные сборы:",
          "item1": "Управленческие сборы за надзор за недвижимостью",
          "item2": "Транзакционные сборы за инвестиции",
          "item3": "Сборы за конвертацию валюты при необходимости"
        },
        "intellectual": {
          "title": "Интеллектуальная собственность",
          "description": "Весь контент на платформе, включая тексты, графику, логотипы и программное обеспечение, является собственностью Israel Prime Estate и защищён законами об интеллектуальной собственности."
        },
        "privacySection": {
          "title": "Конфиденциальность",
          "description": "Использование вами платформы также регулируется нашей Политикой конфиденциальности, которая описывает, как мы собираем, используем и защищаем вашу личную информацию."
        },
        "termination": {
          "title": "Прекращение",
          "description": "Мы оставляем за собой право прекратить или приостановить действие вашего аккаунта в любое время за нарушение этих условий или по любой другой причине, которую мы сочтём уместной."
        },
        "governing": {
          "title": "Применимое право",
          "description": "Эти условия регулируются законодательством Израиля. Любые споры разрешаются в судах Тель-Авива, Израиль."
        },
        "contact": {
          "title": "Контактная информация",
          "intro": "По вопросам об этих условиях, пожалуйста, свяжитесь с нами:",
          "email": "Email",
          "phone": "Телефон",
          "address": "Адрес"
        }
      }
    }
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr', 'es', 'zh', 'he', 'ru'],
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    }
  })

createRoot(document.getElementById("root")!).render(
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
    <App />
  </ThemeProvider>
)
